import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Ban,
  BellOff,
  BookOpen,
  Bot,
  BrainCircuit,
  Bug,
  Building2,
  Check,
  CheckCheck,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock3,
  Copy,
  CornerUpLeft,
  Download,
  ExternalLink,
  Eye,
  File,
  FileSpreadsheet,
  FileText,
  Flag,
  FlaskConical,
  Forward,
  GraduationCap,
  ImagePlus,
  Loader2,
  Maximize2,
  Megaphone,
  MessageSquare,
  Mic,
  Minimize2,
  Minus,
  Moon,
  MoreHorizontal,
  MoreVertical,
  Music,
  Palette,
  Paperclip,
  Pin,
  PinOff,
  Plus,
  Presentation,
  RefreshCw,
  Route,
  School,
  Search,
  Send,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Square,
  Star,
  StickyNote,
  Sun,
  Timer,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  Video,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuthStore } from '../../store/authStore';
import { api, extractApiErrorMessage } from '../../api/client';
import { Modal } from '../ui/Modal';
import { UserAvatar } from '../ui/UserAvatar';
import '../ui/datatable.css';
import './chatTheme.css';
import { createChatRealtimeClient, type ChatRealtimeEvent } from './chatRealtimeClient';

import { ChatSegmentTabs } from './ChatSegmentTabs';
import { ChatToolbar } from './ChatToolbar';
import {
  CHAT_GROUPS,
  CHAT_SUB_TABS,
  type ChatGroup,
  type ChatSubFilter,
  classifyGroup,
  countUnreadForGroup,
  countUnreadForSubFilter,
  filterBySegment,
  formatRole,
  getSegmentVisual,
  getSingleRoleName,
} from './chatSegmentConfig';

type ChatMessageType = 'TEXTO' | 'IMAGEM' | 'VIDEO' | 'AUDIO' | 'DOCUMENTO' | 'FEEDBACK' | 'COMUNICADO' | 'CONTATO';

interface ConversaAtiva {
  id: string;
  nome: string;
  perfil: string;
  cargo: string | null;
  fotoUrl?: string | null;
  escolaNome: string | null;
  municipioNome?: string | null;
  ultimoTexto: string;
  atualizadoEm: string;
  unreadCount: number;
}

interface ContatoHierarquico {
  id: string;
  nome: string;
  perfil: string;
  cargo: string | null;
  fotoUrl?: string | null;
  escolaId?: string | null;
  municipioId?: string | null;
  escola?: { id?: string; nome: string } | null;
  municipio?: { id?: string; nome: string } | null;
}

interface MensagemInterna {
  id: string;
  remetenteId: string;
  destinatarioId: string;
  texto: string;
  lida: boolean;
  criadoEm: string;
  remetente: {
    id: string;
    nome: string;
    perfil: string;
    cargo?: string | null;
    fotoUrl?: string | null;
  };
  tipo?: ChatMessageType;
  arquivoUrl?: string | null;
  arquivoNome?: string | null;
  arquivoMime?: string | null;
  arquivoTamanho?: number | null;
  statusOperacional?: LocalTicketStatus | null;
  entregueEm?: string | null;
  lidaEm?: string | null;
  protocolo?: string | null;
  metadata?: Record<string, any> | null;
  pending?: boolean;
  error?: boolean;
}

export interface GlobalChatWidgetProps {
  isOpen?: boolean;
  onClose?: () => void;
  isDocked?: boolean;
  onToggleDock?: () => void;
  chatUnreadCount?: number;
  setChatUnreadCount?: (count: number) => void;
  embedded?: boolean;
  defaultContatoId?: string | null;
  isStandalone?: boolean;
}

const QUICK_REPLIES = [
  'Recebido. Vou verificar.',
  'Pode me enviar um vídeo do problema?',
  'Consegue mandar um print da tela?',
  'Estou acompanhando por aqui.',
  'Resolvido. Pode testar novamente.',
];

const QUICK_REPLIES_BY_GROUP: Record<ChatGroup, string[]> = {
  admin: [
    'Recebido, vou validar no painel.',
    'Preciso conferir permissão e vínculo.',
    'Vou verificar logs e retorno.',
  ],
  operadores: [
    'Pode me enviar um vídeo do problema?',
    'Consegue mandar um print da tela?',
    'Vou validar com a escola agora.',
    'Após o ajuste, peça para testar novamente.',
  ],
  familia: [
    'Olá, recebemos sua mensagem.',
    'Vamos encaminhar para a escola responsável.',
    'Pode informar o nome do aluno e turma?',
    'Obrigado pelo retorno.',
  ],
  clientes: [
    'Recebido. Vou verificar.',
    'Pode me mandar evidência em vídeo?',
    'Vou acionar a operação técnica.',
    'Resolvido. Pode testar novamente.',
  ],
};

type ThemePreset = 'azul' | 'violeta' | 'verde' | 'laranja' | 'noturno';

type LocalTicketStatus = 'ABERTO' | 'ANALISANDO' | 'PENDENTE' | 'ARRUMANDO' | 'TESTE' | 'RESOLVIDO';

type ChatPriority = 'BAIXA' | 'NORMAL' | 'ALTA' | 'URGENTE';

const THEME_PRESETS: Array<{ key: ThemePreset; label: string }> = [
  { key: 'azul', label: 'Azul institucional' },
  { key: 'violeta', label: 'Violeta premium' },
  { key: 'verde', label: 'Verde família' },
  { key: 'laranja', label: 'Laranja gestão' },
  { key: 'noturno', label: 'Noturno' },
];

const TICKET_STATUS: Array<{ key: LocalTicketStatus; label: string; color: string; icon: React.ElementType }> = [
  { key: 'ABERTO', label: 'Aberto', color: '#2563eb', icon: Clock3 },
  { key: 'ANALISANDO', label: 'Analisando', color: '#7c3aed', icon: Search },
  { key: 'PENDENTE', label: 'Pendente', color: '#d97706', icon: Clock3 },
  { key: 'ARRUMANDO', label: 'Arrumando', color: '#ea580c', icon: Wrench },
  { key: 'TESTE', label: 'Teste', color: '#0891b2', icon: FlaskConical },
  { key: 'RESOLVIDO', label: 'Resolvido', color: '#16a34a', icon: CheckCircle2 },
];

const PRIORITY_OPTIONS: Array<{ key: ChatPriority; label: string; color: string }> = [
  { key: 'BAIXA', label: 'Baixa', color: '#64748b' },
  { key: 'NORMAL', label: 'Normal', color: '#2563eb' },
  { key: 'ALTA', label: 'Alta', color: '#d97706' },
  { key: 'URGENTE', label: 'Urgente', color: '#dc2626' },
];

const SLA_PRESETS = [
  { label: '2h', hours: 2 },
  { label: '4h', hours: 4 },
  { label: '24h', hours: 24 },
];

const ATTACH_ACTIONS: Array<{
  key: 'ARQUIVO' | 'CONTATO';
  label: string;
  sublabel: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  shadow: string;
}> = [
  {
    key: 'ARQUIVO',
    label: 'Arquivo ou Mídia',
    sublabel: 'Fotos, vídeos, áudios, PDFs e docs',
    icon: Paperclip,
    color: '#ffffff',
    bg: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
    shadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
  },
  {
    key: 'CONTATO',
    label: 'Compartilhar Contato',
    sublabel: 'Cartão de membro da escola',
    icon: UserPlus,
    color: '#ffffff',
    bg: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
    shadow: '0 4px 14px rgba(139, 92, 246, 0.4)',
  },
];

interface PendingAttachment {
  file: File;
  previewUrl?: string;
  tipo: ChatMessageType;
  nome: string;
  tamanho: number;
}

const COMPOSER_TYPES: Array<{ key: ChatMessageType; label: string; icon: React.ElementType }> = [
  { key: 'TEXTO', label: 'Mensagem', icon: MessageSquare },
  { key: 'FEEDBACK', label: 'Feedback', icon: Bug },
  { key: 'COMUNICADO', label: 'Comunicado', icon: Sparkles },
];

const DEFAULT_FLOAT_WIDTH = 472;
const DEFAULT_FLOAT_HEIGHT = 700;
const DOCK_WIDTH = 480;

function readLocalJson<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function writeLocalJson<T>(key: string, value: T) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // armazenamento local pode estar bloqueado
  }
}

function truncateSegment(text: string, max = 20): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max).trimEnd() + '...';
}

function formatBreadcrumb(...segments: (string | null | undefined)[]): string {
  return segments
    .filter(Boolean)
    .map((s) => truncateSegment(s as string, 20))
    .join(' > ');
}

function getStatusConfig(status?: LocalTicketStatus) {
  return TICKET_STATUS.find((item) => item.key === status) || TICKET_STATUS[0];
}

function getPriorityConfig(priority?: ChatPriority) {
  return PRIORITY_OPTIONS.find((item) => item.key === priority) || PRIORITY_OPTIONS[1];
}

function formatSlaRemaining(value?: string | null) {
  const target = safeDate(value);
  if (!target) return { label: 'Sem SLA', mode: 'neutral' as const };
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { label: 'SLA vencido', mode: 'danger' as const };
  const minutes = Math.ceil(diff / 60000);
  if (minutes < 60) return { label: `${minutes} min`, mode: minutes <= 30 ? 'warning' as const : 'ok' as const };
  const hours = Math.ceil(minutes / 60);
  return { label: `${hours}h restantes`, mode: hours <= 2 ? 'warning' as const : 'ok' as const };
}

function getQuickReplies(group: ChatGroup): string[] {
  return QUICK_REPLIES_BY_GROUP[group] || QUICK_REPLIES;
}

function safeDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTime(value?: string | null): string {
  const date = safeDate(value);
  if (!date) return '--:--';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(value?: string | null): string {
  const date = safeDate(value);
  if (!date) return '';

  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((startToday - startDate) / 86400000);

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function getInitials(nome: string): string {
  if (!nome) return '??';
  const clean = nome.replace(/^(Prof\.|Profa\.|Profª|Profº|Dir\.|Coord\.)\s+/i, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatPreviewText(text: string): string {
  if (!text) return '';
  if (text.startsWith('[AC_CONTACT_CARD]')) {
    try {
      const data = JSON.parse(text.replace('[AC_CONTACT_CARD]', ''));
      return `👤 Contato: ${data.nome}`;
    } catch {
      return '👤 Contato compartilhado';
    }
  }
  return text;
}

function truncateText(value: string, max = 58): string {
  const formatted = formatPreviewText(value);
  if (!formatted) return '';
  return formatted.length > max ? `${formatted.slice(0, max - 1)}…` : formatted;
}

function formatFileSize(size?: number | null): string {
  if (!size || size <= 0) return '';
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

async function compressImageIfNeeded(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const maxDim = 1920;
        let width = img.width;
        let height = img.height;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              try {
                const compressedFile = new (window as any).File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, '.jpg'),
                  {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  },
                ) as File;
                resolve(compressedFile);
              } catch {
                resolve(file);
              }
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.82,
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

function getFileIcon(filename?: string | null, tipo?: string) {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  if (tipo === 'AUDIO' || ext === 'mp3' || ext === 'wav' || ext === 'ogg' || ext === 'm4a') {
    return <Music size={18} style={{ color: '#a855f7' }} />;
  }
  if (tipo === 'VIDEO' || ext === 'mp4' || ext === 'webm' || ext === 'mov') {
    return <Video size={18} style={{ color: '#ec4899' }} />;
  }
  if (ext === 'pdf') {
    return <FileText size={18} style={{ color: '#ef4444' }} />;
  }
  if (ext === 'doc' || ext === 'docx') {
    return <FileText size={18} style={{ color: '#3b82f6' }} />;
  }
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') {
    return <FileSpreadsheet size={18} style={{ color: '#10b981' }} />;
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return <Presentation size={18} style={{ color: '#f59e0b' }} />;
  }
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'webp') {
    return <ImagePlus size={18} style={{ color: '#06b6d4' }} />;
  }
  return <File size={18} style={{ color: '#94a3b8' }} />;
}

function groupMessagesByDay(messages: MensagemInterna[]) {
  const blocks: Array<{ day: string; messages: MensagemInterna[] }> = [];

  for (const message of messages) {
    const day = formatDay(message.criadoEm) || 'Sem data';
    const last = blocks[blocks.length - 1];
    if (last?.day === day) {
      last.messages.push(message);
    } else {
      blocks.push({ day, messages: [message] });
    }
  }

  return blocks;
}

//#region AC_BLOCK global-chat-widget.skeleton-list -- Skeleton do chat global
// @ac-purpose Renderiza placeholders de carregamento reutilizados pelo widget global de chat.
// @ac-related react-app/src/components/chat/GlobalChatWidget.tsx#GlobalChatWidget
// @ac-tests node tools/codex-kit.mjs check react-tsc
function SkeletonList() {
  return (
    <div style={{ padding: 14, display: 'grid', gap: 12 }}>
      {Array.from({ length: 7 }).map((_, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="ac-chat-skeleton" style={{ width: 42, height: 42, borderRadius: 999 }} />
          <div style={{ flex: 1, display: 'grid', gap: 7 }}>
            <div className="ac-chat-skeleton" style={{ width: '62%', height: 11 }} />
            <div className="ac-chat-skeleton" style={{ width: '38%', height: 9 }} />
            <div className="ac-chat-skeleton" style={{ width: '76%', height: 9 }} />
          </div>
        </div>
      ))}
    </div>
  );
}
//#endregion AC_BLOCK global-chat-widget.skeleton-list

//#region AC_BLOCK global-chat-widget.global-chat-widget -- Widget global de chat
// @ac-purpose Controla conversas, contatos, nao lidos, escalacao e janela global de chat montada no layout autenticado.
// @ac-related react-app/src/components/layout/BaseLayout.tsx#BaseLayout
// @ac-related react-app/src/components/chat/production/chatRoutes.tsx#GlobalChatWidget
// @ac-related docs_secutiry/matriz-rotas-repetidas-reducao.md#Push-e-FCM
// @ac-routes authenticated-layout, chat-widget
// @ac-tests node tools/codex-kit.mjs check react-tsc
export function GlobalChatWidget({
  isOpen = true,
  onClose,
  isDocked = false,
  onToggleDock,
  chatUnreadCount = 0,
  setChatUnreadCount,
  embedded = false,
  defaultContatoId = null,
  isStandalone = false,
}: GlobalChatWidgetProps) {
  const user = useAuthStore((s) => s.user);

  const [viewMode, setViewMode] = useState<'conversas' | 'contatos' | 'historico' | 'anexos' | 'chat'>('conversas');
  const [viewFilter, setViewFilter] = useState<'todos' | 'categoria'>('categoria');
  const [activeGroup, setActiveGroup] = useState<ChatGroup | 'todos'>('todos');
  const [activeSubFilter, setActiveSubFilter] = useState<ChatSubFilter>('todos');

  // Histórico e Central de Anexos
  const [historicoConversas, setHistoricoConversas] = useState<any[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [historicoSearch, setHistoricoSearch] = useState('');
  const [historicoSubTab, setHistoricoSubTab] = useState<'encerrados' | 'bloqueados'>('encerrados');

  const [anexosList, setAnexosList] = useState<any[]>([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [anexosFilterTipo, setAnexosFilterTipo] = useState<string>('TODOS');
  const [anexosSearch, setAnexosSearch] = useState('');

  // Ações de Mensagens: Citação/Quote e Seleção Múltipla em Lote
  const [replyingToMessage, setReplyingToMessage] = useState<{ id: string; texto: string; remetenteNome: string; tipo: string } | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);

  // Modais de Ações de Mensagens
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardMessageIds, setForwardMessageIds] = useState<string[]>([]);
  const [forwardSelectedContacts, setForwardSelectedContacts] = useState<string[]>([]);
  const [forwardSearch, setForwardSearch] = useState('');
  const [forwarding, setForwarding] = useState(false);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteTargetMessageIds, setDeleteTargetMessageIds] = useState<string[]>([]);
  const [deletingMessages, setDeletingMessages] = useState(false);

  // Gestão de Contato: Fixar, Silenciar, Bloquear
  const [pinnedContactIds, setPinnedContactIds] = useState<string[]>(() => readLocalJson<string[]>('ac-chat-pinned-v1', []));
  const [mutedContactIds, setMutedContactIds] = useState<Record<string, string>>(() => readLocalJson<Record<string, string>>('ac-chat-muted-v1', {}));
  const [blockedContactIds, setBlockedContactIds] = useState<string[]>(() => readLocalJson<string[]>('ac-chat-blocked-v1', []));
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [muteTargetContactId, setMuteTargetContactId] = useState<string | null>(null);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    if (!isStandalone) return;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isStandalone]);

  useEffect(() => {
    writeLocalJson('ac-chat-pinned-v1', pinnedContactIds);
  }, [pinnedContactIds]);

  useEffect(() => {
    writeLocalJson('ac-chat-muted-v1', mutedContactIds);
  }, [mutedContactIds]);

  useEffect(() => {
    writeLocalJson('ac-chat-blocked-v1', blockedContactIds);
  }, [blockedContactIds]);

  const [serverConversas, setServerConversas] = useState<ConversaAtiva[]>([]);
  const [localEmptyConversas, setLocalEmptyConversas] = useState<ConversaAtiva[]>(() => {
    if (typeof window !== 'undefined' && user?.id) {
      try {
        const stored = localStorage.getItem(`ac_chat_empty_started_${user.id}`);
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const [hiddenConversaIds, setHiddenConversaIds] = useState<string[]>(() => {
    if (typeof window !== 'undefined' && user?.id) {
      return readLocalJson<string[]>(`ac_chat_hidden_${user.id}`, []);
    }
    return [];
  });

  useEffect(() => {
    if (user?.id) {
      writeLocalJson(`ac_chat_hidden_${user.id}`, hiddenConversaIds);
    }
  }, [hiddenConversaIds, user?.id]);

  const conversas = useMemo(() => {
    const hiddenSet = new Set(hiddenConversaIds);
    const serverIds = new Set(serverConversas.map((c) => c.id));
    const filteredLocal = localEmptyConversas.filter((lc) => !serverIds.has(lc.id) && !hiddenSet.has(lc.id));
    const filteredServer = serverConversas.filter((sc) => !hiddenSet.has(sc.id));
    
    if (filteredLocal.length !== localEmptyConversas.length && user?.id) {
      window.setTimeout(() => {
        try {
          localStorage.setItem(`ac_chat_empty_started_${user.id}`, JSON.stringify(filteredLocal));
          setLocalEmptyConversas(filteredLocal);
        } catch {}
      }, 0);
    }
    
    return [...filteredLocal, ...filteredServer];
  }, [serverConversas, localEmptyConversas, hiddenConversaIds, user?.id]);
  const [contatos, setContatos] = useState<ContatoHierarquico[]>([]);
  const [messages, setMessages] = useState<MensagemInterna[]>([]);

  const [selectedContatoId, setSelectedContatoId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [draftMessage, setDraftMessage] = useState('');

  const [loadingConversas, setLoadingConversas] = useState(false);
  const [loadingContatos, setLoadingContatos] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(true);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);

  // V3 — preferências locais do operador. Não exige backend.
  const [themePreset, setThemePreset] = useState<ThemePreset>(() => readLocalJson<ThemePreset>('ac-chat-theme-v3', 'azul'));
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(() => {
    return readLocalJson<'dark' | 'light'>('ac-chat-theme-mode-v1', 'dark');
  });

  useEffect(() => {
    writeLocalJson('ac-chat-theme-mode-v1', themeMode);
  }, [themeMode]);

  const toggleThemeMode = () => {
    setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => readLocalJson<string[]>('ac-chat-favorites-v3', []));
  const [activeMenuContactId, setActiveMenuContactId] = useState<string | null>(null);

  useEffect(() => {
    const handleGlobalClick = () => {
      setActiveMenuContactId(null);
    };
    if (activeMenuContactId) {
      window.addEventListener('click', handleGlobalClick);
      return () => window.removeEventListener('click', handleGlobalClick);
    }
  }, [activeMenuContactId]);

  const [statusByContact, setStatusByContact] = useState<Record<string, LocalTicketStatus>>(() => readLocalJson<Record<string, LocalTicketStatus>>('ac-chat-status-v3', {}));
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [showMediaTray, setShowMediaTray] = useState(false);
  const [draftType, setDraftType] = useState<ChatMessageType>('TEXTO');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [typingByContact, setTypingByContact] = useState<Record<string, number>>({});
  const [showOpsPanel, setShowOpsPanel] = useState(false);
  const [priorityByContact, setPriorityByContact] = useState<Record<string, ChatPriority>>(() => readLocalJson<Record<string, ChatPriority>>('ac-chat-priority-v6', {}));
  const [slaByContact, setSlaByContact] = useState<Record<string, string>>(() => readLocalJson<Record<string, string>>('ac-chat-sla-v6', {}));
  const [internalNote, setInternalNote] = useState('');
  const [automationSummary, setAutomationSummary] = useState('');
  const [automationRunning, setAutomationRunning] = useState(false);
  const [categoryByContact, setCategoryByContact] = useState<Record<string, string>>(() => readLocalJson<Record<string, string>>('ac-chat-category-v7', {}));
  const [copilotSuggestion, setCopilotSuggestion] = useState('');
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [channelByContact, setChannelByContact] = useState<Record<string, string>>(() => readLocalJson<Record<string, string>>('ac-chat-channel-v8', {}));
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => readLocalJson<Record<string, boolean>>('ac-chat-accordion-collapsed-v1', {}));

  const hasAutoRoutedOnOpenRef = useRef(false);

  useEffect(() => {
    writeLocalJson('ac-chat-accordion-collapsed-v1', collapsedGroups);
  }, [collapsedGroups]);

  const toggleGroupCollapse = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const isCurrentlyCollapsed = prev[groupId] !== undefined ? prev[groupId] : true;
      return {
        ...prev,
        [groupId]: !isCurrentlyCollapsed,
      };
    });
  }, []);

  // Custom premium dialog states
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [transferSearch, setTransferSearch] = useState('');
  const [closeReason, setCloseReason] = useState('Resolvido pelo atendimento');
  const [closeCustomReason, setCloseCustomReason] = useState('');
  const [escalateReason, setEscalateReason] = useState('SLA/urgência operacional');
  const [escalateCustomReason, setEscalateCustomReason] = useState('');
  const [showAttachContactModal, setShowAttachContactModal] = useState(false);
  const [attachContactSearch, setAttachContactSearch] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; title: string } | null>(null);

  const [uploadQuota, setUploadQuota] = useState<{
    maxFileSizeMb: number;
    maxFileSizeBytes: number;
    dailyUploadLimitMb: number;
    dailyUploadLimitBytes: number;
    usedTodayBytes: number;
    usedTodayMb: number;
    remainingBytes: number;
    remainingMb: number;
    isUnlimited: boolean;
  }>({
    maxFileSizeMb: 25,
    maxFileSizeBytes: 25 * 1024 * 1024,
    dailyUploadLimitMb: 100,
    dailyUploadLimitBytes: 100 * 1024 * 1024,
    usedTodayBytes: 0,
    usedTodayMb: 0,
    remainingBytes: 100 * 1024 * 1024,
    remainingMb: 100,
    isUnlimited: (user?.perfil || '').toUpperCase() === 'ADMIN',
  });

  const loadUploadQuota = useCallback(async () => {
    try {
      const res = await api.get('/chat-interno/minha-cota');
      if (res.data) {
        setUploadQuota(res.data);
      }
    } catch {
      // Fallback silencioso
    }
  }, []);

  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const typingTimerRef = useRef<number | null>(null);
  const lastTypingSentRef = useRef(0);

  const loadConversas = useCallback(async (showLoader = false) => {
    if (showLoader) setLoadingConversas(true);
    try {
      const res = await api.get('/chat-interno/conversas');
      const filtered = (res.data || []).filter((c: any) => c.perfil !== 'ALUNO');
      setServerConversas(filtered);

      // Auto-roteamento inteligente ao abrir o chat:
      // Se há mensagens não lidas de APENAS UMA pessoa, abre direto na conversa dela.
      // Se há 2 ou mais pessoas com mensagens não lidas (ou múltiplas conversas), abre na lista de conversas.
      // Se não há conversas no início, abre a lista de contatos.
      if (!hasAutoRoutedOnOpenRef.current && !selectedContatoId && !defaultContatoId) {
        hasAutoRoutedOnOpenRef.current = true;
        const unreadConversas = filtered.filter((c: any) => (c.unreadCount || 0) > 0);

        if (unreadConversas.length === 1) {
          setSelectedContatoId(unreadConversas[0].id);
          setViewMode('chat');
        } else if (unreadConversas.length === 0 && filtered.length === 1) {
          setSelectedContatoId(filtered[0].id);
          setViewMode('chat');
        } else if (filtered.length === 0) {
          setViewMode('contatos');
        } else {
          setViewMode('conversas');
        }
      }
    } catch {
      // polling silencioso para não incomodar o operador
    } finally {
      if (showLoader) setLoadingConversas(false);
    }
  }, [selectedContatoId, defaultContatoId]);

  const loadMessages = useCallback(async (contatoId?: string | null, showLoader = false) => {
    const targetId = contatoId || selectedContatoId;
    if (!targetId) return;

    if (showLoader) setLoadingMessages(true);
    try {
      const res = await api.get(`/chat-interno/mensagens/${targetId}`);
      setMessages(res.data || []);
      loadConversas(false);
    } catch {
      // polling silencioso
    } finally {
      if (showLoader) setLoadingMessages(false);
    }
  }, [loadConversas, selectedContatoId]);

  const loadContatos = useCallback(async () => {
    setLoadingContatos(true);
    try {
      const res = await api.get('/chat-interno/contatos');
      const filtered = (res.data || []).filter((c: any) => c.perfil !== 'ALUNO');
      setContatos(filtered);
    } catch {
      toast.error('Erro ao listar catálogo de contatos');
    } finally {
      setLoadingContatos(false);
    }
  }, []);

  const loadHistorico = useCallback(async () => {
    setLoadingHistorico(true);
    try {
      const res = await api.get('/chat-interno/historico');
      setHistoricoConversas(res.data || []);
    } catch {
      // Fallback silencioso
    } finally {
      setLoadingHistorico(false);
    }
  }, []);

  const loadAnexos = useCallback(async (tipo = anexosFilterTipo) => {
    setLoadingAnexos(true);
    try {
      const res = await api.get('/chat-interno/anexos', {
        params: { tipo: tipo !== 'TODOS' ? tipo : undefined },
      });
      setAnexosList(res.data || []);
    } catch {
      // Fallback silencioso
    } finally {
      setLoadingAnexos(false);
    }
  }, [anexosFilterTipo]);

  useEffect(() => {
    if (viewMode === 'historico') {
      loadHistorico();
    } else if (viewMode === 'anexos') {
      loadAnexos();
    }
  }, [viewMode, loadHistorico, loadAnexos]);


  const formatRelativeTime = useCallback((dateStr?: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) return 'agora';
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHour / 24);

    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `há ${diffMin} min`;
    if (diffHour < 24) {
      if (date.getDate() === now.getDate()) {
        return `há ${diffHour} h`;
      }
      return 'ontem';
    }
    if (diffDays === 1) return 'ontem';
    if (diffDays < 7) return `há ${diffDays} dias`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }, []);

  const handleCopyMessage = useCallback((text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success('Mensagem copiada para a área de transferência');
  }, []);

  const handleReplyMessage = useCallback((msg: MensagemInterna) => {
    const senderName = msg.remetenteId === user?.id ? 'Você' : (msg.remetente?.nome || 'Contato');
    setReplyingToMessage({
      id: msg.id,
      texto: msg.texto || (msg.arquivoNome ? `[${msg.tipo || 'ANEXO'}] ${msg.arquivoNome}` : `[${msg.tipo || 'MENSAGEM'}]`),
      remetenteNome: senderName,
      tipo: msg.tipo || 'TEXTO',
    });
    composerRef.current?.focus();
  }, [user?.id]);

  const handleStartForward = useCallback((msgIds: string[]) => {
    if (!msgIds.length) return;
    setForwardMessageIds(msgIds);
    setForwardSelectedContacts([]);
    setForwardSearch('');
    setShowForwardModal(true);
  }, []);

  const handleExecuteForward = useCallback(async () => {
    if (!forwardMessageIds.length || !forwardSelectedContacts.length) {
      toast.warning('Selecione pelo menos um contato para encaminhar.');
      return;
    }
    setForwarding(true);
    try {
      await api.post('/chat-interno/mensagens/encaminhar-lote', {
        messageIds: forwardMessageIds,
        destinatarioIds: forwardSelectedContacts,
      });
      toast.success(`${forwardMessageIds.length} mensagem(ns) encaminhada(s) com sucesso!`);
      setShowForwardModal(false);
      setIsSelectionMode(false);
      setSelectedMessageIds([]);
      loadConversas(false);
      if (selectedContatoId && forwardSelectedContacts.includes(selectedContatoId)) {
        loadMessages(selectedContatoId);
      }
    } catch {
      toast.error('Erro ao encaminhar mensagens.');
    } finally {
      setForwarding(false);
    }
  }, [forwardMessageIds, forwardSelectedContacts, loadConversas, loadMessages, selectedContatoId]);

  const handleStartDelete = useCallback((msgIds: string[]) => {
    if (!msgIds.length) return;
    setDeleteTargetMessageIds(msgIds);
    setShowDeleteConfirmModal(true);
  }, []);

  const handleExecuteDelete = useCallback(async () => {
    if (!deleteTargetMessageIds.length) return;
    setDeletingMessages(true);
    try {
      await api.post('/chat-interno/mensagens/apagar-lote', {
        messageIds: deleteTargetMessageIds,
      });
      setMessages((prev) =>
        prev.map((m) =>
          deleteTargetMessageIds.includes(m.id)
            ? {
                ...m,
                texto: '🚫 Esta mensagem foi apagada',
                metadata: {
                  ...((m.metadata as any) || {}),
                  isRemovido: true,
                  removidoEm: new Date().toISOString(),
                },
              }
            : m,
        ),
      );
      toast.success('Mensagem(ns) apagada(s) com sucesso.');
      setShowDeleteConfirmModal(false);
      setIsSelectionMode(false);
      setSelectedMessageIds([]);
      loadConversas(false);
    } catch {
      toast.error('Erro ao apagar mensagens.');
    } finally {
      setDeletingMessages(false);
    }
  }, [deleteTargetMessageIds, loadConversas]);

  const handleTogglePin = useCallback(async (contatoId: string, currentPinned: boolean) => {
    try {
      const nextPinned = !currentPinned;
      setPinnedContactIds((prev) =>
        nextPinned ? [contatoId, ...prev.filter((id) => id !== contatoId)] : prev.filter((id) => id !== contatoId),
      );
      await api.post(`/chat-interno/conversas/${contatoId}/fixar`, { fixado: nextPinned });
      toast.success(nextPinned ? 'Conversa fixada no topo' : 'Conversa desafixada');
      loadConversas(false);
    } catch {
      toast.error('Erro ao atualizar status de fixação.');
    }
  }, [loadConversas]);

  const handleExecuteMute = useCallback(async (contatoId: string, duracaoHoras: number) => {
    try {
      await api.post(`/chat-interno/conversas/${contatoId}/silenciar`, { duracaoHoras });
      let silenciadoAte: string | null = null;
      if (duracaoHoras === -1) {
        silenciadoAte = new Date(Date.now() + 100 * 365 * 24 * 3600 * 1000).toISOString();
      } else if (duracaoHoras > 0) {
        silenciadoAte = new Date(Date.now() + duracaoHoras * 3600 * 1000).toISOString();
      }
      setMutedContactIds((prev) => {
        const next = { ...prev };
        if (silenciadoAte) next[contatoId] = silenciadoAte;
        else delete next[contatoId];
        return next;
      });
      setShowMuteModal(false);
      toast.success(duracaoHoras === 0 ? 'Notificações reativadas' : 'Conversa silenciada com sucesso');
      loadConversas(false);
    } catch {
      toast.error('Erro ao alterar silenciamento.');
    }
  }, [loadConversas]);

  const handleToggleBlock = useCallback(async (contatoId: string, currentBlocked: boolean) => {
    try {
      const nextBlocked = !currentBlocked;
      setBlockedContactIds((prev) =>
        nextBlocked ? [...prev.filter((id) => id !== contatoId), contatoId] : prev.filter((id) => id !== contatoId),
      );
      await api.post(`/chat-interno/conversas/${contatoId}/bloquear`, {
        bloqueado: nextBlocked,
        motivo: 'Bloqueado pelo operador',
      });
      if (nextBlocked) {
        setServerConversas((prev) => prev.filter((c) => c.id !== contatoId));
        setContatos((prev) => prev.filter((c) => c.id !== contatoId));
        if (selectedContatoId === contatoId) {
          setSelectedContatoId(null);
          setViewMode('conversas');
        }
        toast.success('Contato bloqueado e movido para o Histórico');
      } else {
        toast.success('Contato desbloqueado com sucesso');
      }
      loadConversas(false);
      loadContatos();
      loadHistorico();
    } catch {
      toast.error('Erro ao alterar bloqueio do contato.');
    }
  }, [loadConversas, loadContatos, loadHistorico, selectedContatoId]);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, textarea')) return;
    e.preventDefault();
    hasDragged.current = false;
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    if (!isDocked) {
      isDragging.current = true;
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      }
    }
  }, [isDocked]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) hasDragged.current = true;
      if (!isDragging.current) return;

      const x = e.clientX - dragOffset.current.x;
      const y = e.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - DEFAULT_FLOAT_WIDTH;
      const maxY = window.innerHeight - 110;

      setDragPos({
        x: Math.max(8, Math.min(x, maxX)),
        y: Math.max(8, Math.min(y, maxY)),
      });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      setTimeout(() => { hasDragged.current = false; }, 0);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    setDragPos(null);
  }, [isDocked]);

  useEffect(() => {
    writeLocalJson('ac-chat-theme-v3', themePreset);
  }, [themePreset]);

  useEffect(() => {
    writeLocalJson('ac-chat-favorites-v3', favoriteIds);
  }, [favoriteIds]);

  useEffect(() => {
    writeLocalJson('ac-chat-status-v3', statusByContact);
  }, [statusByContact]);

  useEffect(() => {
    writeLocalJson('ac-chat-priority-v6', priorityByContact);
  }, [priorityByContact]);

  useEffect(() => {
    writeLocalJson('ac-chat-sla-v6', slaByContact);
  }, [slaByContact]);

  useEffect(() => {
    writeLocalJson('ac-chat-category-v7', categoryByContact);
  }, [categoryByContact]);

  useEffect(() => {
    writeLocalJson('ac-chat-channel-v8', channelByContact);
  }, [channelByContact]);

  useEffect(() => {
    if (!isOpen) {
      hasAutoRoutedOnOpenRef.current = false;
      return;
    }
    if (defaultContatoId) {
      setSelectedContatoId(defaultContatoId);
      setViewMode('chat');
      hasAutoRoutedOnOpenRef.current = true;
    } else if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const paramId = params.get('contatoId');
      if (paramId) {
        setSelectedContatoId(paramId);
        setViewMode('chat');
        hasAutoRoutedOnOpenRef.current = true;
      }
    }
  }, [isOpen, defaultContatoId]);

  useEffect(() => {
    if (!isOpen || !user) return;
    loadConversas(conversas.length === 0);
    loadContatos();
    loadUploadQuota();
    const delay = realtimeConnected ? 30000 : 5000;
    const interval = window.setInterval(() => loadConversas(false), delay);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, loadConversas, loadContatos, loadUploadQuota, realtimeConnected]);

  useEffect(() => {
    if (!isOpen || !selectedContatoId || viewMode !== 'chat') return;
    loadMessages(selectedContatoId, messages.length === 0);
    const delay = realtimeConnected ? 30000 : 3000;
    const interval = window.setInterval(() => loadMessages(selectedContatoId, false), delay);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedContatoId, viewMode, loadMessages, realtimeConnected]);

  useEffect(() => {
    if (viewMode === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      window.setTimeout(markVisibleMessagesAsRead, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, viewMode, selectedContatoId]);

  useEffect(() => {
    const totalUnread = conversas.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    setChatUnreadCount?.(totalUnread);
  }, [conversas, setChatUnreadCount]);

  // Auto-expande o textarea de envio conforme ganha novas linhas (mínimo de 64px/2 linhas até 120px/5 linhas, depois usa scroll)
  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const nextHeight = Math.max(Math.min(scrollHeight, 120), 64);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = scrollHeight > 120 ? 'auto' : 'hidden';
  }, [draftMessage]);

  useEffect(() => {
    if (!isOpen || !user) return;

    const client = createChatRealtimeClient({
      onOpen: () => setRealtimeConnected(true),
      onClose: () => setRealtimeConnected(false),
      onError: () => setRealtimeConnected(false),
      onEvent: (event: ChatRealtimeEvent) => {
        if (event.type === 'MESSAGE_CREATED') {
          const message = event.payload as MensagemInterna;
          setMessages((prev) => {
            const belongsToActiveChat = selectedContatoId && (message.remetenteId === selectedContatoId || message.destinatarioId === selectedContatoId);
            if (!belongsToActiveChat) return prev;
            if (prev.some((m) => m.id === message.id)) return prev;

            // Evitar duplicidade devido à race condition com o POST HTTP
            if (message.remetenteId === user?.id) {
              const pendingIdx = prev.findIndex((m) => m.id.startsWith('tmp-') && m.texto === message.texto);
              if (pendingIdx !== -1) {
                return prev.map((m, idx) => (idx === pendingIdx ? message : m));
              }
            }

            return [...prev, message];
          });
          loadConversas(false);
        }

        if (event.type === 'MESSAGE_READ') {
          const payload = event.payload as { messageId: string; lidaEm?: string; entregueEm?: string };
          setMessages((prev) => prev.map((m) => m.id === payload.messageId ? { ...m, lidaEm: payload.lidaEm || m.lidaEm, entregueEm: payload.entregueEm || m.entregueEm } : m));
        }

        if (event.type === 'STATUS_UPDATED') {
          const payload = event.payload as { contatoId: string; statusOperacional: LocalTicketStatus };
          setStatusByContact((prev) => ({ ...prev, [payload.contatoId]: payload.statusOperacional }));
        }

        if (event.type === 'FAVORITE_UPDATED') {
          const payload = event.payload as { contatoId: string; favorito: boolean };
          setFavoriteIds((prev) => payload.favorito ? Array.from(new Set([...prev, payload.contatoId])) : prev.filter((id) => id !== payload.contatoId));
        }

        if (event.type === 'TYPING') {
          const payload = event.payload as { contatoId: string; until?: number };
          setTypingByContact((prev) => ({ ...prev, [payload.contatoId]: payload.until || Date.now() + 2500 }));
        }

        if (event.type === 'STOP_TYPING') {
          const payload = event.payload as { contatoId: string };
          setTypingByContact((prev) => ({ ...prev, [payload.contatoId]: 0 }));
        }
      },
    });

    return () => client.close();
  }, [isOpen, user, selectedContatoId, loadConversas]);

  const selectedConversa = conversas.find((c) => c.id === selectedContatoId);
  const selectedContatoFromList = contatos.find((c) => c.id === selectedContatoId);

  const activeChatPartnerName = selectedConversa?.nome || selectedContatoFromList?.nome || 'Conversa';
  const activeChatPartnerPerfil = selectedConversa?.perfil || selectedContatoFromList?.perfil || '';
  const activeChatPartnerCargo = selectedConversa?.cargo ?? selectedContatoFromList?.cargo ?? null;
  const activeChatPartnerEscola = selectedConversa?.escolaNome || selectedContatoFromList?.escola?.nome || null;
  const activeChatPartnerMunicipio = selectedConversa?.municipioNome || selectedContatoFromList?.municipio?.nome || null;
  const activePartnerPhoto = selectedConversa?.fotoUrl || selectedContatoFromList?.fotoUrl || null;
  const activeChatPartnerSub = formatRole(activeChatPartnerPerfil, activeChatPartnerCargo);
  const activeVisual = getSegmentVisual(activeChatPartnerPerfil, activeChatPartnerCargo);
  const activeChatGroup = classifyGroup(activeChatPartnerPerfil, activeChatPartnerCargo);
  const activeQuickReplies = useMemo(() => getQuickReplies(activeChatGroup), [activeChatGroup]);

  useEffect(() => {
    if (!isStandalone) return;
    if (selectedContatoId && activeChatPartnerName && activeChatPartnerName !== 'Conversa') {
      document.title = `CHAT: ${activeChatPartnerName.toUpperCase()}`;
    } else {
      document.title = 'Chat Interno — Aluno Conectado';
    }

    return () => {
      if (isStandalone) {
        document.title = 'Chat Interno — Aluno Conectado';
      }
    };
  }, [isStandalone, selectedContatoId, activeChatPartnerName]);
  const activeTicketStatus = selectedContatoId ? (statusByContact[selectedContatoId] || 'ABERTO') : 'ABERTO';
  const activeTicketStatusConfig = getStatusConfig(activeTicketStatus);
  const activePriority = selectedContatoId ? (priorityByContact[selectedContatoId] || 'NORMAL') : 'NORMAL';
  const activePriorityConfig = getPriorityConfig(activePriority);
  const activeSla = selectedContatoId ? slaByContact[selectedContatoId] : undefined;
  const activeSlaInfo = formatSlaRemaining(activeSla);
  const activeCategory = selectedContatoId ? categoryByContact[selectedContatoId] : undefined;
  const activeChannel = selectedContatoId ? (channelByContact[selectedContatoId] || 'APP') : 'APP';

  const textFilteredConversas = useMemo(() => {
    if (!searchText.trim()) return conversas;
    const q = searchText.toLowerCase();
    return conversas.filter((c) =>
      c.nome.toLowerCase().includes(q) ||
      c.perfil.toLowerCase().includes(q) ||
      (c.escolaNome && c.escolaNome.toLowerCase().includes(q)) ||
      (c.cargo && c.cargo.toLowerCase().includes(q)) ||
      (c.ultimoTexto && c.ultimoTexto.toLowerCase().includes(q))
    );
  }, [conversas, searchText]);

  const textFilteredContatos = useMemo(() => {
    if (!searchText.trim()) return contatos;
    const q = searchText.toLowerCase();
    return contatos.filter((c) =>
      c.nome.toLowerCase().includes(q) ||
      c.perfil.toLowerCase().includes(q) ||
      (c.escola?.nome && c.escola.nome.toLowerCase().includes(q)) ||
      (c.cargo && c.cargo.toLowerCase().includes(q))
    );
  }, [contatos, searchText]);

  const filteredContatosForTransfer = useMemo(() => {
    if (!transferSearch.trim()) return contatos;
    const q = transferSearch.toLowerCase();
    return contatos.filter((c) =>
      c.nome.toLowerCase().includes(q) ||
      c.perfil.toLowerCase().includes(q) ||
      (c.escola?.nome && c.escola.nome.toLowerCase().includes(q)) ||
      (c.cargo && c.cargo.toLowerCase().includes(q))
    );
  }, [contatos, transferSearch]);

  const filteredContatosForAttach = useMemo(() => {
    if (!attachContactSearch.trim()) return contatos;
    const q = attachContactSearch.toLowerCase();
    return contatos.filter((c) =>
      c.nome.toLowerCase().includes(q) ||
      c.perfil.toLowerCase().includes(q) ||
      (c.escola?.nome && c.escola.nome.toLowerCase().includes(q)) ||
      (c.cargo && c.cargo.toLowerCase().includes(q))
    );
  }, [contatos, attachContactSearch]);

  const segmentedConversas = useMemo(() => {
    let base = activeGroup === 'todos'
      ? textFilteredConversas
      : filterBySegment(textFilteredConversas, activeGroup as ChatGroup, activeSubFilter);

    if (onlyUnread) base = base.filter((c) => c.unreadCount > 0);
    if (onlyFavorites) base = base.filter((c) => favoriteIds.includes(c.id));

    return [...base].sort((a, b) => {
      // 1. Contatos Fixados SEMPRE no topo absoluto (último fixado fica em primeiro)
      const aPinnedIndex = pinnedContactIds.indexOf(a.id);
      const bPinnedIndex = pinnedContactIds.indexOf(b.id);
      const isAPinned = aPinnedIndex !== -1;
      const isBPinned = bPinnedIndex !== -1;

      if (isAPinned && !isBPinned) return -1;
      if (!isAPinned && isBPinned) return 1;
      if (isAPinned && isBPinned) {
        return aPinnedIndex - bPinnedIndex;
      }

      // 2. Mensagens não lidas
      const unreadDiff = (b.unreadCount || 0) - (a.unreadCount || 0);
      if (unreadDiff !== 0) return unreadDiff;

      // 3. Ordem cronológica por data de atualização
      return (safeDate(b.atualizadoEm)?.getTime() || 0) - (safeDate(a.atualizadoEm)?.getTime() || 0);
    });
  }, [textFilteredConversas, activeGroup, activeSubFilter, onlyUnread, onlyFavorites, favoriteIds, pinnedContactIds]);

  const getConversaContextLabel = useCallback((conversa: ConversaAtiva): string => {
    const myProfile = (user?.perfil || '').toUpperCase();
    const p = (conversa.perfil || '').toUpperCase();
    const cargo = (conversa.cargo || '').toLowerCase();

    const isDiretoria = (
      p === 'ESCOLA' &&
      ['diretor', 'diretora', 'diretoria', 'vice-diretor', 'vice diretor', 'vice-diretora', 'vice diretora', 'gestor'].some((term) =>
        cargo.includes(term)
      )
    );

    const isEquipeEscolar = (
      !isDiretoria &&
      (p === 'ESCOLA' ||
        ['secretaria', 'secretário', 'secretária', 'coordenador', 'coordenadora', 'coordenação', 'supervisor', 'supervisora', 'orientador', 'orientadora', 'apoio'].some((term) =>
          cargo.includes(term)
        ))
    );

    const isProfessor = p === 'PROFESSOR';
    const isResponsavel = p === 'RESPONSAVEL' || p === 'RESPONSÁVEL' || p === 'ALUNO';

    if (p === 'ADMIN') {
      return myProfile === 'ADMIN' ? 'Minha Equipe' : 'Suporte';
    }

    if (
      (myProfile === 'MUNICIPAL' && p === 'MUNICIPAL') ||
      (myProfile === 'ESCOLA' && p === 'ESCOLA' && (conversa.escolaNome ? conversa.escolaNome === (user as any)?.escolaNome || conversa.escolaNome === (user as any)?.escola?.nome : true)) ||
      (myProfile === 'ESTADUAL' && p === 'ESTADUAL') ||
      (myProfile === 'REDE' && p === 'REDE')
    ) {
      return 'Minha Equipe';
    }

    if (p === 'REDE') return formatBreadcrumb('Administradores', 'Rede');
    if (p === 'ESTADUAL') return formatBreadcrumb('Administradores', 'Estadual');
    if (p === 'MUNICIPAL') return 'Secretaria de Educação (SME)';

    const isSchoolUser = myProfile === 'ESCOLA' || myProfile === 'PROFESSOR';
    const userSchoolName = (user as any)?.escolaNome || (user as any)?.escola?.nome;
    const isMySchool = isSchoolUser && (!conversa.escolaNome || conversa.escolaNome === userSchoolName);

    if (conversa.escolaNome) {
      if (isDiretoria) return isMySchool ? formatBreadcrumb('Minha Escola', 'Diretoria') : formatBreadcrumb('Escolas', conversa.escolaNome, 'Diretorias');
      if (isEquipeEscolar) return isMySchool ? formatBreadcrumb('Minha Escola', 'Secretaria') : formatBreadcrumb('Escolas', conversa.escolaNome, 'Equipes');
      if (isProfessor) return isMySchool ? formatBreadcrumb('Minha Escola', 'Professores') : formatBreadcrumb('Escolas', conversa.escolaNome, 'Professores');
      if (isResponsavel) return isMySchool ? formatBreadcrumb('Minha Escola', 'Alunos & Famílias') : formatBreadcrumb('Escolas', conversa.escolaNome, 'Alunos & Famílias');
      return isMySchool ? 'Minha Escola' : formatBreadcrumb('Escolas', conversa.escolaNome);
    }

    if (p === 'ESCOLA') {
      if (isDiretoria) return 'Diretoria & Coordenação';
      if (isEquipeEscolar) return 'Secretaria & Apoio';
      return 'Minha Escola';
    }

    if (isProfessor) return 'Professores';
    if (isResponsavel) return 'Alunos & Famílias';

    return getSingleRoleName(conversa.perfil, conversa.cargo);
  }, [user?.perfil, (user as any)?.escolaNome, (user as any)?.escola?.nome]);

  const segmentedContatos = useMemo(() => {
    if (activeGroup === 'todos') return textFilteredContatos;
    return filterBySegment(textFilteredContatos, activeGroup as ChatGroup, activeSubFilter);
  }, [textFilteredContatos, activeGroup, activeSubFilter]);

  const hierarchicalContactGroups = useMemo(() => {
    const myProfile = (user?.perfil || '').toUpperCase();

    // Classificadores dos 4 subníveis da Escola
    const isDiretoria = (c: ContatoHierarquico) => {
      const p = (c.perfil || '').toUpperCase();
      const cargo = (c.cargo || '').toLowerCase();
      return (
        p === 'ESCOLA' &&
        ['diretor', 'diretora', 'diretoria', 'vice-diretor', 'vice diretor', 'vice-diretora', 'vice diretora', 'gestor'].some((term) =>
          cargo.includes(term)
        )
      );
    };

    const isEquipeEscolar = (c: ContatoHierarquico) => {
      const p = (c.perfil || '').toUpperCase();
      const cargo = (c.cargo || '').toLowerCase();
      if (isDiretoria(c)) return false;
      return (
        p === 'ESCOLA' ||
        ['secretaria', 'secretário', 'secretária', 'coordenador', 'coordenadora', 'coordenação', 'supervisor', 'supervisora', 'orientador', 'orientadora', 'apoio'].some((term) =>
          cargo.includes(term)
        )
      );
    };

    const isProfessor = (c: ContatoHierarquico) => {
      return (c.perfil || '').toUpperCase() === 'PROFESSOR';
    };

    const isResponsavel = (c: ContatoHierarquico) => {
      const p = (c.perfil || '').toUpperCase();
      return p === 'RESPONSAVEL' || p === 'RESPONSÁVEL' || p === 'ALUNO';
    };

    const adminMaster: ContatoHierarquico[] = [];
    const adminRede: ContatoHierarquico[] = [];
    const adminEstadual: ContatoHierarquico[] = [];
    const adminMunicipal: ContatoHierarquico[] = [];
    const minhaEquipe: ContatoHierarquico[] = [];
    const escolasMap = new Map<string, { nome: string; contatos: ContatoHierarquico[] }>();
    const outros: ContatoHierarquico[] = [];

    for (const contato of segmentedContatos) {
      const p = (contato.perfil || '').toUpperCase();

      if (p === 'ADMIN') {
        if (myProfile === 'ADMIN') {
          minhaEquipe.push(contato);
        } else {
          adminMaster.push(contato);
        }
        continue;
      }

      if (
        (myProfile === 'MUNICIPAL' && p === 'MUNICIPAL') ||
        (myProfile === 'ESCOLA' && p === 'ESCOLA' && (contato.escolaId ? contato.escolaId === (user as any)?.escolaId : (contato.escola?.nome && (contato.escola.nome === (user as any)?.escolaNome || contato.escola.nome === (user as any)?.escola?.nome)))) ||
        (myProfile === 'ESTADUAL' && p === 'ESTADUAL') ||
        (myProfile === 'REDE' && p === 'REDE')
      ) {
        minhaEquipe.push(contato);
        continue;
      }

      if (p === 'REDE') {
        adminRede.push(contato);
        continue;
      }

      if (p === 'ESTADUAL') {
        adminEstadual.push(contato);
        continue;
      }

      if (p === 'MUNICIPAL') {
        adminMunicipal.push(contato);
        continue;
      }

      const escolaNome = contato.escola?.nome;
      if (escolaNome) {
        if (!escolasMap.has(escolaNome)) {
          escolasMap.set(escolaNome, { nome: escolaNome, contatos: [] });
        }
        escolasMap.get(escolaNome)!.contatos.push(contato);
        continue;
      }

      outros.push(contato);
    }

    const groups: Array<{
      id: string;
      title: string;
      icon: any;
      color: string;
      bgBadge: string;
      totalCount: number;
      singleContact?: ContatoHierarquico;
      singleContextLabel?: string;
      subGroups?: Array<{
        id: string;
        title: string;
        icon: any;
        color: string;
        bgBadge: string;
        contacts: ContatoHierarquico[];
        singleContact?: ContatoHierarquico;
        singleContextLabel?: string;
      }>;
      contacts?: ContatoHierarquico[];
    }> = [];

    // 1. Suporte Técnico (Administrador Master)
    if (adminMaster.length === 1) {
      const single = adminMaster[0];
      groups.push({
        id: `group_suporte_${single.id}`,
        title: 'Suporte',
        icon: Shield,
        color: '#8b5cf6',
        bgBadge: 'rgba(139, 92, 246, 0.15)',
        totalCount: 1,
        singleContact: single,
        singleContextLabel: 'Suporte',
      });
    } else if (adminMaster.length >= 2) {
      groups.push({
        id: 'group_suporte',
        title: 'Suporte',
        icon: Shield,
        color: '#8b5cf6',
        bgBadge: 'rgba(139, 92, 246, 0.15)',
        totalCount: adminMaster.length,
        contacts: adminMaster,
      });
    }

    // 2. Secretaria Municipal de Educação (SME / Municipal)
    if (adminMunicipal.length === 1) {
      const single = adminMunicipal[0];
      groups.push({
        id: `group_sme_${single.id}`,
        title: 'Secretaria de Educação (SME)',
        icon: Building2,
        color: '#10b981',
        bgBadge: 'rgba(16, 185, 129, 0.15)',
        totalCount: 1,
        singleContact: single,
        singleContextLabel: 'Secretaria de Educação (SME)',
      });
    } else if (adminMunicipal.length >= 2) {
      groups.push({
        id: 'group_sme',
        title: 'Secretaria de Educação (SME)',
        icon: Building2,
        color: '#10b981',
        bgBadge: 'rgba(16, 185, 129, 0.15)',
        totalCount: adminMunicipal.length,
        contacts: adminMunicipal,
      });
    }

    // 3. Minha Equipe (para membros do mesmo setor/órgão)
    if (minhaEquipe.length === 1) {
      groups.push({
        id: `group_minha_equipe_${minhaEquipe[0].id}`,
        title: 'Minha Equipe',
        icon: Building2,
        color: '#3b82f6',
        bgBadge: 'rgba(59, 130, 246, 0.15)',
        totalCount: 1,
        singleContact: minhaEquipe[0],
        singleContextLabel: 'Minha Equipe',
      });
    } else if (minhaEquipe.length >= 2) {
      groups.push({
        id: 'group_minha_equipe',
        title: 'Minha Equipe',
        icon: Building2,
        color: '#3b82f6',
        bgBadge: 'rgba(59, 130, 246, 0.15)',
        totalCount: minhaEquipe.length,
        contacts: minhaEquipe,
      });
    }

    // 4. Instâncias Estaduais e Rede (se existirem para Master/Rede/Estadual)
    if (adminEstadual.length > 0) {
      if (adminEstadual.length === 1) {
        groups.push({
          id: `group_estadual_${adminEstadual[0].id}`,
          title: 'Gestão Estadual (SEDUC)',
          icon: Building2,
          color: '#3b82f6',
          bgBadge: 'rgba(59, 130, 246, 0.15)',
          totalCount: 1,
          singleContact: adminEstadual[0],
          singleContextLabel: 'Gestão Estadual (SEDUC)',
        });
      } else {
        groups.push({
          id: 'group_estadual',
          title: 'Gestão Estadual (SEDUC)',
          icon: Building2,
          color: '#3b82f6',
          bgBadge: 'rgba(59, 130, 246, 0.15)',
          totalCount: adminEstadual.length,
          contacts: adminEstadual,
        });
      }
    }

    if (adminRede.length > 0) {
      if (adminRede.length === 1) {
        groups.push({
          id: `group_rede_${adminRede[0].id}`,
          title: 'Gestão da Rede',
          icon: Building2,
          color: '#06b6d4',
          bgBadge: 'rgba(6, 182, 212, 0.15)',
          totalCount: 1,
          singleContact: adminRede[0],
          singleContextLabel: 'Gestão da Rede',
        });
      } else {
        groups.push({
          id: 'group_rede',
          title: 'Gestão da Rede',
          icon: Building2,
          color: '#06b6d4',
          bgBadge: 'rgba(6, 182, 212, 0.15)',
          totalCount: adminRede.length,
          contacts: adminRede,
        });
      }
    }

    // 5. Escolas (com "Minha Escola" contextual)
    const sortedEscolas = Array.from(escolasMap.values()).sort((a, b) => a.nome.localeCompare(b.nome));
    const isSchoolUser = myProfile === 'ESCOLA' || myProfile === 'PROFESSOR';
    const userSchoolName = (user as any)?.escolaNome || (user as any)?.escola?.nome;

    for (const esc of sortedEscolas) {
      const isMySchool = isSchoolUser && (sortedEscolas.length === 1 || esc.nome === userSchoolName);
      const rootTitle = isMySchool ? 'Minha Escola' : formatBreadcrumb('Escolas', esc.nome);

      const diretoria = esc.contatos.filter(isDiretoria);
      const equipe = esc.contatos.filter(isEquipeEscolar);
      const professores = esc.contatos.filter(isProfessor);
      const responsaveis = esc.contatos.filter(isResponsavel);
      const outrosEscola = esc.contatos.filter(
        (c) => !isDiretoria(c) && !isEquipeEscolar(c) && !isProfessor(c) && !isResponsavel(c)
      );

      const rawSubGroups = [
        { id: `group_escola_${esc.nome}_diretoria`, title: isMySchool ? 'Diretoria & Coordenação' : 'Diretorias', icon: UserCheck, color: '#2563eb', bgBadge: 'rgba(37, 99, 235, 0.15)', contacts: diretoria },
        { id: `group_escola_${esc.nome}_equipe`, title: isMySchool ? 'Secretaria & Apoio' : 'Equipes', icon: Building2, color: '#f59e0b', bgBadge: 'rgba(245, 158, 11, 0.15)', contacts: [...equipe, ...outrosEscola] },
        { id: `group_escola_${esc.nome}_professores`, title: isMySchool ? (myProfile === 'PROFESSOR' ? 'Colegas Professores' : 'Professores') : 'Professores', icon: GraduationCap, color: '#10b981', bgBadge: 'rgba(16, 185, 129, 0.15)', contacts: professores },
        { id: `group_escola_${esc.nome}_responsaveis`, title: isMySchool ? 'Alunos & Famílias' : 'Responsáveis', icon: Users, color: '#06b6d4', bgBadge: 'rgba(6, 182, 212, 0.15)', contacts: responsaveis },
      ].filter((s) => s.contacts.length > 0);

      const totalEscola = esc.contatos.length;

      if (totalEscola === 1) {
        const singleSub = rawSubGroups[0];
        const single = singleSub.contacts[0];
        const singleLabel = isMySchool ? formatBreadcrumb('Minha Escola', singleSub.title) : formatBreadcrumb('Escolas', esc.nome, singleSub.title);
        groups.push({
          id: `group_esc_${esc.nome}_${single.id}`,
          title: singleLabel,
          icon: singleSub.icon,
          color: singleSub.color,
          bgBadge: singleSub.bgBadge,
          totalCount: 1,
          singleContact: single,
          singleContextLabel: singleLabel,
        });
      } else if (totalEscola >= 2) {
        const processedSubGroups = rawSubGroups.map((sub) => {
          if (sub.contacts.length === 1) {
            return {
              ...sub,
              title: truncateSegment(sub.title, 25),
              singleContact: sub.contacts[0],
              singleContextLabel: isMySchool ? truncateSegment(sub.title, 25) : truncateSegment(sub.title, 20),
            };
          }
          return {
            ...sub,
            title: truncateSegment(sub.title, 25),
          };
        });

        groups.push({
          id: `group_escola_${esc.nome}`,
          title: rootTitle,
          icon: School,
          color: '#f59e0b',
          bgBadge: 'rgba(245, 158, 11, 0.15)',
          totalCount: totalEscola,
          subGroups: processedSubGroups,
        });
      }
    }

    if (outros.length === 1) {
      groups.push({
        id: `group_outros_${outros[0].id}`,
        title: formatBreadcrumb('Outros Contatos'),
        icon: Users,
        color: '#64748b',
        bgBadge: 'rgba(100, 116, 139, 0.15)',
        totalCount: 1,
        singleContact: outros[0],
        singleContextLabel: formatBreadcrumb('Outros Contatos'),
      });
    } else if (outros.length >= 2) {
      groups.push({
        id: 'group_outros',
        title: formatBreadcrumb('Outros Contatos'),
        icon: Users,
        color: '#64748b',
        bgBadge: 'rgba(100, 116, 139, 0.15)',
        totalCount: outros.length,
        contacts: outros,
      });
    }

    return groups;
  }, [segmentedContatos, activeGroup, activeSubFilter, user]);

  const groupBadges = useMemo(() => {
    const badges: Record<string, number> = {};
    for (const group of CHAT_GROUPS) {
      badges[group.key] = countUnreadForGroup(conversas, group.key as ChatGroup);
    }
    return badges;
  }, [conversas]);

  const subBadges = useMemo(() => {
    const badges: Record<string, number> = {};
    const subTabs = activeGroup !== 'todos' ? (CHAT_SUB_TABS[activeGroup] || []) : [];
    for (const sub of subTabs) {
      badges[sub.key] = countUnreadForSubFilter(conversas, activeGroup as ChatGroup, sub.key as ChatSubFilter);
    }
    return badges;
  }, [conversas, activeGroup]);

  const currentSubTabs = activeGroup !== 'todos' ? (CHAT_SUB_TABS[activeGroup] || []) : [];
  const activeIsTyping = Boolean(selectedContatoId && typingByContact[selectedContatoId] && typingByContact[selectedContatoId] > Date.now());

  const showCategoryTabs = viewFilter === 'categoria' && viewMode !== 'chat';
  const showSubTabs = showCategoryTabs && currentSubTabs.length > 0;
  const messageBlocks = useMemo(() => {
    const sorted = [...messages].sort((a, b) => {
      const da = safeDate(a.criadoEm)?.getTime() || 0;
      const db = safeDate(b.criadoEm)?.getTime() || 0;
      return da - db;
    });
    return groupMessagesByDay(sorted);
  }, [messages]);

  const cycleTheme = () => {
    const currentIndex = THEME_PRESETS.findIndex((preset) => preset.key === themePreset);
    const nextPreset = THEME_PRESETS[(currentIndex + 1) % THEME_PRESETS.length];
    setThemePreset(nextPreset.key);
    toast.success(`Tema: ${nextPreset.label}`);
  };

  const toggleFavorite = (id: string) => {
    const nextFavorito = !favoriteIds.includes(id);
    setFavoriteIds((prev) => nextFavorito ? [...prev, id] : prev.filter((item) => item !== id));

    api.patch(`/chat-interno/conversas/${id}/favorito`, { favorito: nextFavorito })
      .catch(() => {
        // Mantém local para não quebrar a operação caso o backend V4 ainda não esteja publicado.
      });
  };

  const updateTicketStatus = async (status: LocalTicketStatus) => {
    if (!selectedContatoId) return;
    const previous = statusByContact[selectedContatoId];
    setStatusByContact((prev) => ({ ...prev, [selectedContatoId]: status }));

    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/status`, { statusOperacional: status });
    } catch {
      if (previous) {
        setStatusByContact((prev) => ({ ...prev, [selectedContatoId]: previous }));
      }
      toast.error('Não foi possível salvar o status no backend.');
    }
  };

  const updatePriority = async (priority: ChatPriority) => {
    if (!selectedContatoId) return;
    const previous = priorityByContact[selectedContatoId];
    setPriorityByContact((prev) => ({ ...prev, [selectedContatoId]: priority }));

    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/prioridade`, { prioridade: priority });
    } catch {
      if (previous) setPriorityByContact((prev) => ({ ...prev, [selectedContatoId]: previous }));
      toast.error('Não foi possível salvar a prioridade.');
    }
  };

  const updateSla = async (hours: number) => {
    if (!selectedContatoId) return;
    const slaVenceEm = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    setSlaByContact((prev) => ({ ...prev, [selectedContatoId]: slaVenceEm }));

    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/sla`, { slaVenceEm });
    } catch {
      toast.error('Não foi possível salvar o SLA.');
    }
  };

  const saveInternalNote = async () => {
    if (!selectedContatoId || !internalNote.trim()) return;
    const texto = internalNote.trim();
    setInternalNote('');

    try {
      await api.post(`/chat-interno/conversas/${selectedContatoId}/notas-internas`, { texto });
      toast.success('Nota interna salva');
    } catch {
      setInternalNote(texto);
      toast.error('Não foi possível salvar a nota interna.');
    }
  };

  const transferConversation = () => {
    if (!selectedContatoId) return;
    setSelectedTransferId(null);
    setTransferSearch('');
    if (contatos.length === 0) {
      loadContatos();
    }
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async () => {
    if (!selectedContatoId || !selectedTransferId) return;
    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/transferir`, {
        responsavelId: selectedTransferId,
      });
      toast.success('Atendimento transferido');
      setShowTransferModal(false);
      loadConversas(false);
    } catch {
      toast.error('Não foi possível transferir o atendimento.');
    }
  };

  const closeConversation = () => {
    if (!selectedContatoId) return;
    setCloseReason('Resolvido pelo atendimento');
    setCloseCustomReason('');
    setShowCloseModal(true);
  };

  const handleCloseSubmit = async () => {
    if (!selectedContatoId) return;
    const motivoFinal = closeReason === 'Outro' ? closeCustomReason.trim() : closeReason;
    if (!motivoFinal) {
      toast.error('Informe o motivo do fechamento.');
      return;
    }

    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/fechar`, {
        motivoFechamento: motivoFinal,
      });
      setStatusByContact((prev) => ({ ...prev, [selectedContatoId]: 'RESOLVIDO' }));
      toast.success('Atendimento fechado');
      setShowCloseModal(false);
      loadConversas(false);
    } catch {
      toast.error('Não foi possível fechar o atendimento.');
    }
  };

  const classifyConversation = async () => {
    if (!selectedContatoId) return;
    setAutomationRunning(true);
    try {
      const res = await api.post(`/chat-interno/conversas/${selectedContatoId}/classificar`);
      const categoria = res.data?.categoria || 'Atendimento';
      setCategoryByContact((prev) => ({ ...prev, [selectedContatoId]: categoria }));
      toast.success(`Categoria: ${categoria}`);
    } catch {
      toast.error('Não foi possível classificar o atendimento.');
    } finally {
      setAutomationRunning(false);
    }
  };

  const summarizeConversation = async () => {
    if (!selectedContatoId) return;
    setAutomationRunning(true);
    try {
      const res = await api.post(`/chat-interno/conversas/${selectedContatoId}/resumir`);
      setAutomationSummary(res.data?.resumo || 'Resumo não disponível.');
    } catch {
      toast.error('Não foi possível gerar resumo operacional.');
    } finally {
      setAutomationRunning(false);
    }
  };

  const escalateConversation = () => {
    if (!selectedContatoId) return;
    setEscalateReason('SLA/urgência operacional');
    setEscalateCustomReason('');
    setShowEscalateModal(true);
  };

  const handleEscalateSubmit = async () => {
    if (!selectedContatoId) return;
    const motivoFinal = escalateReason === 'Outro' ? escalateCustomReason.trim() : escalateReason;
    if (!motivoFinal) {
      toast.error('Informe o motivo da escalação.');
      return;
    }

    setAutomationRunning(true);
    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/escalar`, {
        motivo: motivoFinal,
      });
      toast.success('Atendimento escalado');
      setShowEscalateModal(false);
      loadConversas(false);
    } catch {
      toast.error('Não foi possível escalar o atendimento.');
    } finally {
      setAutomationRunning(false);
    }
  };

  const reopenConversation = async () => {
    if (!selectedContatoId) return;
    setAutomationRunning(true);
    try {
      await api.patch(`/chat-interno/conversas/${selectedContatoId}/reabrir`);
      setStatusByContact((prev) => ({ ...prev, [selectedContatoId]: 'ABERTO' }));
      toast.success('Atendimento reaberto');
    } catch {
      toast.error('Não foi possível reabrir o atendimento.');
    } finally {
      setAutomationRunning(false);
    }
  };

  const runAutomations = async () => {
    setAutomationRunning(true);
    try {
      await api.post('/chat-interno/automacoes/executar');
      toast.success('Automações executadas');
    } catch {
      toast.error('Não foi possível executar automações.');
    } finally {
      setAutomationRunning(false);
    }
  };

  const suggestReply = async () => {
    if (!selectedContatoId) return;
    setCopilotLoading(true);
    try {
      const res = await api.post(`/chat-interno/conversas/${selectedContatoId}/sugerir-resposta`);
      const resposta = res.data?.resposta || '';
      setCopilotSuggestion(resposta);
      if (resposta) setDraftMessage(resposta);
    } catch {
      toast.error('Não foi possível sugerir resposta.');
    } finally {
      setCopilotLoading(false);
    }
  };

  const runTriage = async () => {
    if (!selectedContatoId) return;
    setCopilotLoading(true);
    try {
      const res = await api.post(`/chat-interno/conversas/${selectedContatoId}/triagem`);
      const categoria = res.data?.categoria || activeCategory || 'Atendimento';
      const canal = res.data?.canal || activeChannel;
      setCategoryByContact((prev) => ({ ...prev, [selectedContatoId]: categoria }));
      setChannelByContact((prev) => ({ ...prev, [selectedContatoId]: canal }));
      setCopilotSuggestion(res.data?.resumo || 'Triagem concluída.');
    } catch {
      toast.error('Não foi possível executar triagem.');
    } finally {
      setCopilotLoading(false);
    }
  };

  const searchKnowledge = async () => {
    const query = window.prompt('Buscar na base de conhecimento:');
    if (!query?.trim()) return;
    setCopilotLoading(true);
    try {
      const res = await api.get('/chat-interno/knowledge/search', { params: { q: query.trim() } });
      const first = res.data?.[0];
      setCopilotSuggestion(first ? `${first.titulo}

${first.resumo || first.conteudo || ''}` : 'Nenhum artigo encontrado.');
    } catch {
      toast.error('Não foi possível buscar na base de conhecimento.');
    } finally {
      setCopilotLoading(false);
    }
  };

  const handleAttachAction = (actionKey: 'ARQUIVO' | 'CONTATO') => {
    if (actionKey === 'CONTATO') {
      setShowMediaTray(false);
      setAttachContactSearch('');
      setShowAttachContactModal(true);
      if (contatos.length === 0) loadContatos().catch(() => {});
      return;
    }

    if (!selectedContatoId) {
      toast.error('Abra uma conversa antes de anexar arquivos.');
      return;
    }

    setShowMediaTray(false);
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleAttachContact = async (contato: any) => {
    if (!selectedContatoId || !user) return;
    setShowAttachContactModal(false);

    const contactPayload = {
      id: contato.id,
      nome: contato.nome,
      perfil: contato.perfil,
      cargo: contato.cargo || null,
      escolaNome: contato.escola?.nome || null,
    };

    const textToSend = `[AC_CONTACT_CARD]${JSON.stringify(contactPayload)}`;

    const tempId = `tmp-${Date.now()}`;
    const optimisticMessage: MensagemInterna = {
      id: tempId,
      remetenteId: user.id,
      destinatarioId: selectedContatoId,
      texto: textToSend,
      lida: false,
      criadoEm: new Date().toISOString(),
      remetente: {
        id: user.id,
        nome: (user as any).nome || 'Você',
        perfil: user.perfil,
      },
      tipo: 'TEXTO',
      statusOperacional: activeTicketStatus,
      pending: true,
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const res = await api.post('/chat-interno/mensagens', {
        destinatarioId: selectedContatoId,
        texto: textToSend,
        tipo: 'TEXTO',
        statusOperacional: activeTicketStatus,
      });
      setMessages((prev) => prev.map((m) => (m.id === tempId ? res.data : m)));
      loadConversas(false);
    } catch (err) {
      toast.error('Erro ao anexar contato');
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    }
  };

  const renderAttachedContactCard = (texto: string) => {
    try {
      const data = JSON.parse(texto.replace('[AC_CONTACT_CARD]', ''));
      const visual = getSegmentVisual(data.perfil, data.cargo);
      const isMe = data.id === user?.id;

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid var(--ac-chat-border)',
          borderRadius: 14,
          padding: '12px 14px',
          marginTop: 4,
          width: '100%',
          maxWidth: 240,
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <UserAvatar
              src={data.fotoUrl}
              name={data.nome}
              role={data.cargo || data.perfil}
              size={38}
              style={{ flexShrink: 0 }}
            />
            <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
              <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 900, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.nome}>
                {data.nome}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '0.65rem', fontWeight: 750, color: 'rgba(255, 255, 255, 0.9)' }}>
                {formatRole(data.perfil, data.cargo)}
              </p>
              {data.escolaNome && (
                <p style={{ margin: '2px 0 0', fontSize: '0.62rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.75)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.escolaNome}>
                  {data.escolaNome}
                </p>
              )}
            </div>
          </div>
          {!isMe && (
            <button
              type="button"
              onClick={() => handleSelectContact(data.id)}
              style={{
                width: '100%',
                padding: '7px',
                borderRadius: 10,
                border: 0,
                background: '#ffffff',
                color: 'var(--ac-chat-blue, #2563eb)',
                fontSize: '0.68rem',
                fontWeight: 800,
                cursor: 'pointer',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
              }}
            >
              <MessageSquare size={11} style={{ color: 'var(--ac-chat-blue, #2563eb)' }} /> Conversar
            </button>
          )}
        </div>
      );
    } catch {
      return <span>[Contato Inválido]</span>;
    }
  };

  const attachFile = async (rawFile: File) => {
    if (!selectedContatoId) {
      toast.error('Abra uma conversa antes de anexar arquivos.');
      return;
    }

    let fileToAttach = rawFile;
    if (rawFile.type.startsWith('image/')) {
      fileToAttach = await compressImageIfNeeded(rawFile);
    }

    // Validação preventiva 1: Limite máximo por arquivo
    if (fileToAttach.size > uploadQuota.maxFileSizeBytes) {
      toast.error(`O arquivo excede o limite máximo permitido de ${uploadQuota.maxFileSizeMb}MB.`);
      return;
    }

    // Validação preventiva 2: Cota diária restante
    if (!uploadQuota.isUnlimited && fileToAttach.size > uploadQuota.remainingBytes) {
      const fileMb = (fileToAttach.size / (1024 * 1024)).toFixed(2);
      toast.error(`Cota diária insuficiente (${uploadQuota.remainingMb}MB restantes hoje). O arquivo selecionado possui ${fileMb}MB.`);
      return;
    }

    let tipo: ChatMessageType = 'DOCUMENTO';
    if (fileToAttach.type.startsWith('image/')) tipo = 'IMAGEM';
    else if (fileToAttach.type.startsWith('video/')) tipo = 'VIDEO';
    else if (fileToAttach.type.startsWith('audio/')) tipo = 'AUDIO';

    let previewUrl: string | undefined = undefined;
    if (tipo === 'IMAGEM' || tipo === 'VIDEO' || tipo === 'AUDIO') {
      try {
        previewUrl = URL.createObjectURL(fileToAttach);
      } catch {}
    }

    if (pendingAttachment?.previewUrl) {
      try { URL.revokeObjectURL(pendingAttachment.previewUrl); } catch {}
    }

    setPendingAttachment({
      file: fileToAttach,
      previewUrl,
      tipo,
      nome: fileToAttach.name,
      tamanho: fileToAttach.size,
    });
    setShowMediaTray(false);
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  const handleSelectedFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await attachFile(file);
  };

  const handleComposerPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          await attachFile(file);
          break;
        }
      }
    }
  };

  const exportCurrentChat = () => {
    if (!messages.length) {
      toast.info('Esta conversa ainda não tem mensagens para exportar.');
      return;
    }

    const lines = [
      `Atendimento: ${activeChatPartnerName}`,
      `Perfil: ${activeChatPartnerSub}`,
      `Status: ${activeTicketStatusConfig.label}`,
      `Exportado em: ${new Date().toLocaleString('pt-BR')}`,
      '',
      ...messages.map((message) => {
        const mine = message.remetenteId === user?.id ? 'Você' : activeChatPartnerName;
        const tipo = message.tipo || 'TEXTO';
        const file = message.arquivoUrl ? ` | Arquivo: ${message.arquivoNome || message.arquivoUrl}` : '';
        return `[${formatDay(message.criadoEm)} ${formatTime(message.criadoEm)}] ${mine} (${tipo}): ${message.texto || ''}${file}`;
      }),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-${activeChatPartnerName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleOpenContacts = async () => {
    setViewMode('contatos');
    setSearchText('');
    if (contatos.length === 0) await loadContatos();
  };

  const handleSelectContact = (contatoId: string) => {
    // Se estava oculta, desarquiva a conversa
    setHiddenConversaIds((prev) => (prev.includes(contatoId) ? prev.filter((id) => id !== contatoId) : prev));

    setSelectedContatoId(contatoId);
    setViewMode('chat');
    setSearchText('');
    setShowMediaTray(false);
    setDraftType('TEXTO');
    setLoadingMessages(true);

    const contact = contatos.find((c) => c.id === contatoId);
    if (contact) {
      const alreadyInServer = serverConversas.some((sc) => sc.id === contatoId);
      const alreadyInLocal = localEmptyConversas.some((lc) => lc.id === contatoId);
      if (!alreadyInServer && !alreadyInLocal) {
        const newEmpty: ConversaAtiva = {
          id: contact.id,
          nome: contact.nome,
          perfil: contact.perfil,
          cargo: contact.cargo,
          escolaNome: contact.escola?.nome || null,
          municipioNome: contact.municipio?.nome || null,
          ultimoTexto: '',
          atualizadoEm: new Date().toISOString(),
          unreadCount: 0,
        };
        const updatedLocal = [newEmpty, ...localEmptyConversas];
        setLocalEmptyConversas(updatedLocal);
        if (user?.id) {
          try {
            localStorage.setItem(`ac_chat_empty_started_${user.id}`, JSON.stringify(updatedLocal));
          } catch {}
        }
      }
    }

    api.get(`/chat-interno/mensagens/${contatoId}`)
      .then((res) => setMessages(res.data || []))
      .catch(() => toast.error('Erro ao abrir conversa'))
      .finally(() => {
        setLoadingMessages(false);
        setTimeout(() => composerRef.current?.focus(), 60);
      });
  };

  const handleRemoveConversa = async (conversaId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/chat-interno/conversas/${conversaId}/fechar`, {
        motivo: 'Atendimento encerrado pelo operador',
        motivoFechamento: 'Atendimento encerrado pelo operador',
      });
      setServerConversas((prev) => prev.filter((c) => c.id !== conversaId));
      setLocalEmptyConversas((prev) => prev.filter((c) => c.id !== conversaId));
      setHiddenConversaIds((prev) => (prev.includes(conversaId) ? prev : [...prev, conversaId]));
      if (selectedContatoId === conversaId) {
        setSelectedContatoId(null);
        setViewMode('conversas');
      }
      toast.success('Atendimento encerrado e arquivado no Histórico');
      loadHistorico();
      loadConversas(false);
    } catch {
      toast.error('Erro ao encerrar atendimento');
    }
  };

  const goBackToList = () => {
    setViewMode('conversas');
    setSelectedContatoId(null);
    hasAutoRoutedOnOpenRef.current = true;
    setMessages([]);
    setSearchText('');
    setShowMediaTray(false);
    setDraftType('TEXTO');
  };

  const appendQuickReply = (text: string) => {
    setDraftMessage((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
    window.setTimeout(() => composerRef.current?.focus(), 0);
  };

  const notifyTyping = () => {
    if (!selectedContatoId) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1200) return;
    lastTypingSentRef.current = now;

    api.post('/chat-interno/typing', { destinatarioId: selectedContatoId })
      .catch(() => {
        // endpoint V5 opcional; falha silenciosa para não atrapalhar digitação
      });

    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    typingTimerRef.current = window.setTimeout(() => {
      api.post('/chat-interno/stop-typing', { destinatarioId: selectedContatoId }).catch(() => {});
    }, 1800);
  };

  const handleDraftChange = (value: string) => {
    setDraftMessage(value);
    notifyTyping();
  };

  const markVisibleMessagesAsRead = () => {
    if (!selectedContatoId || !messages.length) return;
    const unreadReceived = messages.filter((m) => m.remetenteId === selectedContatoId && !m.lidaEm);
    if (!unreadReceived.length) return;

    api.patch('/chat-interno/mensagens/lidas', {
      contatoId: selectedContatoId,
      messageIds: unreadReceived.map((m) => m.id),
    }).catch(() => {});
  };

  const handleSend = async () => {
    if (!selectedContatoId || sending) return;

    // Caso 1: Envio com Anexo Pendente (Mídia ou Documento)
    if (pendingAttachment) {
      const attachment = pendingAttachment;
      const textToSend = draftMessage.trim();
      const tempId = `tmp-${Date.now()}`;

      const optimisticMessage: MensagemInterna = {
        id: tempId,
        remetenteId: user?.id || 'me',
        destinatarioId: selectedContatoId,
        texto: textToSend || attachment.nome,
        lida: false,
        criadoEm: new Date().toISOString(),
        remetente: {
          id: user?.id || 'me',
          nome: (user as any)?.nome || 'Você',
          perfil: (user as any)?.perfil || 'ADMIN',
        },
        tipo: attachment.tipo,
        statusOperacional: activeTicketStatus,
        arquivoUrl: attachment.previewUrl || null,
        arquivoNome: attachment.nome,
        arquivoTamanho: attachment.tamanho,
        pending: true,
      };

      setSending(true);
      setDraftMessage('');
      setPendingAttachment(null);
      setMessages((prev) => [...prev, optimisticMessage]);

      const formData = new FormData();
      formData.append('destinatarioId', selectedContatoId);
      formData.append('tipo', attachment.tipo);
      formData.append('texto', textToSend);
      formData.append('statusOperacional', activeTicketStatus);
      formData.append('file', attachment.file);

      try {
        const res = await api.post('/chat-interno/midias', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (res.data) {
          setMessages((prev) => prev.map((m) => (m.id === tempId ? res.data : m)));
          loadConversas(false);
          loadUploadQuota();
        }
      } catch (err: any) {
        const errMsg = extractApiErrorMessage(err, 'Erro ao enviar mídia. Verifique o tamanho do arquivo ou formato suportado.');
        toast.error(errMsg);
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, pending: false, error: true } : m)));
      } finally {
        setSending(false);
        requestAnimationFrame(() => {
          composerRef.current?.focus();
        });
      }
      return;
    }

    // Caso 2: Mensagem de Texto Normal
    if (!draftMessage.trim()) return;

    const textToSend = draftMessage.trim();
    const messageType = draftType;
    const tempId = `tmp-${Date.now()}`;
    const replyMeta = replyingToMessage ? { replyTo: replyingToMessage } : undefined;

    const optimisticMessage: MensagemInterna = {
      id: tempId,
      remetenteId: user?.id || 'me',
      destinatarioId: selectedContatoId,
      texto: textToSend,
      lida: false,
      criadoEm: new Date().toISOString(),
      remetente: {
        id: user?.id || 'me',
        nome: (user as any)?.nome || 'Você',
        perfil: (user as any)?.perfil || 'ADMIN',
      },
      tipo: messageType,
      statusOperacional: activeTicketStatus,
      metadata: replyMeta,
      pending: true,
    };

    setSending(true);
    setDraftMessage('');
    setReplyingToMessage(null);
    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const res = await api.post('/chat-interno/mensagens', {
        destinatarioId: selectedContatoId,
        texto: textToSend,
        tipo: messageType,
        statusOperacional: activeTicketStatus,
        metadata: replyMeta,
      });
      setMessages((prev) => prev.map((m) => (m.id === tempId ? res.data : m)));
      setDraftType('TEXTO');
      loadConversas(false);
    } catch {
      setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, pending: false, error: true } : m)));
      setDraftMessage(textToSend);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
      requestAnimationFrame(() => {
        composerRef.current?.focus();
      });
    }
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      requestAnimationFrame(() => {
        composerRef.current?.focus();
      });
    }
  };

  const openExternalWindow = useCallback(async () => {
    const url = selectedContatoId ? `/chat-popout?contatoId=${selectedContatoId}` : '/chat-popout';

    let targetLeft = (window.screen as any).availLeft ?? 0;
    let targetTop = (window.screen as any).availTop ?? 0;
    let targetWidth = window.screen.availWidth || window.screen.width;
    let targetHeight = window.screen.availHeight || window.screen.height;

    // Multi-Screen Window Placement API (Detecção de segundo monitor no Chrome/Edge)
    if (typeof window !== 'undefined' && 'getScreenDetails' in window) {
      try {
        const screenDetails = await (window as any).getScreenDetails();
        if (screenDetails && screenDetails.screens && screenDetails.screens.length > 1) {
          const otherScreen = screenDetails.screens.find((s: any) => s !== screenDetails.currentScreen) || screenDetails.screens[1];
          if (otherScreen) {
            targetLeft = otherScreen.availLeft ?? otherScreen.left ?? targetLeft;
            targetTop = otherScreen.availTop ?? otherScreen.top ?? targetTop;
            targetWidth = otherScreen.availWidth ?? otherScreen.width ?? targetWidth;
            targetHeight = otherScreen.availHeight ?? otherScreen.height ?? targetHeight;
          }
        }
      } catch {
        // Fallback silencioso se o usuário não autorizou detecção de múltiplos monitores
      }
    }

    const newWin = window.open(
      url,
      'ac_chat_external_window',
      `width=${targetWidth},height=${targetHeight},top=${targetTop},left=${targetLeft},popup=yes,toolbar=no,menubar=no,location=no,status=no,directories=no,resizable=yes,scrollbars=yes`
    );

    if (newWin) {
      newWin.focus();
      onClose?.();
    }
  }, [selectedContatoId, onClose]);

  const renderHeader = () => {
    const showBack = (!isStandalone || windowWidth < 680) && viewMode === 'chat';

    return (
      <div
        className="ac-chat-header"
        onMouseDown={isStandalone ? undefined : handleDragStart}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        style={{
          height: isMinimized ? 48 : 62,
          cursor: isDocked || isStandalone ? 'default' : 'grab',
          borderRadius: isMinimized && !isDocked ? 16 : undefined,
          transition: 'height 180ms ease, border-radius 180ms ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            {showBack && !isMinimized && (
              <button type="button" onClick={goBackToList} className="ac-chat-icon-btn" title="Voltar para a lista">
                <ArrowLeft size={17} />
              </button>
            )}

            {!isStandalone && viewMode === 'chat' && !isMinimized ? (
              <>
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <UserAvatar
                    src={activePartnerPhoto}
                    name={activeChatPartnerName}
                    role={activeChatPartnerCargo || activeChatPartnerPerfil}
                    size="sm"
                    style={{ flexShrink: 0 }}
                  />
                  <span className="ac-chat-presence" style={{ position: 'absolute', bottom: -2, right: -2 }} />
                </div>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <h4
                    className="ac-chat-title"
                    title={activeChatPartnerName}
                    style={{
                      margin: 0,
                      fontSize: '0.86rem',
                      fontWeight: 800,
                      color: 'var(--ac-chat-text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {activeChatPartnerName}
                  </h4>
                  <p
                    className="ac-chat-subtitle"
                    style={{
                      margin: '2px 0 0',
                      fontSize: '0.66rem',
                      fontWeight: 600,
                      color: 'var(--ac-chat-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span>{getSingleRoleName(activeChatPartnerPerfil, activeChatPartnerCargo)}</span>
                    {activeChatPartnerEscola && (
                      <>
                        <span style={{ opacity: 0.5 }}>•</span>
                        <span title={activeChatPartnerEscola}>{activeChatPartnerEscola}</span>
                      </>
                    )}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '12px 12px 0 12px',
                    display: isMinimized ? 'none' : 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.20), rgba(59, 130, 246, 0.35))',
                    border: '1.5px solid rgba(147, 51, 234, 0.45)',
                    flexShrink: 0,
                    boxShadow: '0 2px 12px rgba(147, 51, 234, 0.30)',
                    overflow: 'hidden',
                    padding: 3,
                  }}
                >
                  <img
                    src="/icons/icon-192x192.png"
                    alt="Aluno Conectado"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '9px 9px 0 9px',
                    }}
                  />
                </div>
                <div style={{ minWidth: 0, display: isMinimized ? 'none' : 'flex', flexDirection: 'column' }}>
                  <h4 className="ac-chat-title" title="Aluno Conectado" style={{ color: 'var(--ac-chat-text)' }}>
                    Aluno Conectado
                  </h4>
                  <span className="ac-chat-subtitle" style={{ color: 'var(--ac-chat-muted)' }}>
                    Comunicação & Suporte
                  </span>
                </div>
              </>
            )}

            {isMinimized && chatUnreadCount > 0 && <span className="ac-chat-unread">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>}
          </div>

          {/* Controles do Cabeçalho: Toggle Dark/Light, Pop-out Externa, Maximizar e Fechar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <button
              type="button"
              onClick={toggleThemeMode}
              className="ac-chat-icon-btn"
              title={themeMode === 'dark' ? 'Alternar para Modo Claro' : 'Alternar para Modo Escuro'}
            >
              {themeMode === 'dark' ? <Sun size={15} style={{ color: '#fbbf24' }} /> : <Moon size={15} style={{ color: '#6366f1' }} />}
            </button>

            {!embedded && !isStandalone && (
              <button
                type="button"
                onClick={openExternalWindow}
                className="ac-chat-icon-btn"
                title="Abrir em janela externa (Pop-out)"
              >
                <ExternalLink size={15} />
              </button>
            )}

            {!embedded && !isDocked && !isStandalone && (
              <button
                type="button"
                onClick={() => setIsMaximized((prev) => !prev)}
                className="ac-chat-icon-btn"
                title={isMaximized ? 'Restaurar tamanho' : 'Maximizar'}
              >
                {isMaximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            )}

            {onClose && !embedded && !isStandalone && (
              <button type="button" onClick={onClose} className="ac-chat-icon-btn ac-chat-icon-btn-danger" title="Fechar chat">
                <X size={15} />
              </button>
            )}

            {isStandalone && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (windowWidth >= 680) {
                        const targetW = 420;
                        const targetH = Math.min(window.screen.availHeight || 720, 720);
                        const targetL = Math.max(0, Math.round(((window.screen.availWidth || 1024) - targetW) / 2));
                        const targetT = Math.max(0, Math.round(((window.screen.availHeight || 768) - targetH) / 2));
                        window.resizeTo(targetW, targetH);
                        window.moveTo(targetL, targetT);
                      } else {
                        const availL = (window.screen as any).availLeft ?? 0;
                        const availT = (window.screen as any).availTop ?? 0;
                        const availW = window.screen.availWidth || window.screen.width;
                        const availH = window.screen.availHeight || window.screen.height;
                        window.moveTo(availL, availT);
                        window.resizeTo(availW, availH);
                      }
                    } catch {
                      // Fallback silencioso se restrito pelo navegador
                    }
                  }}
                  className="ac-chat-icon-btn"
                  title={windowWidth >= 680 ? 'Alternar para Janela Compacta' : 'Maximizar / Expandir Tela Cheia'}
                >
                  {windowWidth >= 680 ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                </button>

                <button
                  type="button"
                  onClick={() => window.close()}
                  className="ac-chat-icon-btn ac-chat-icon-btn-danger"
                  title="Fechar janela"
                >
                  <X size={15} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRightHeader = () => {
    return (
      <div
        className="ac-chat-header"
        style={{
          height: 62,
          borderBottom: '1px solid var(--ac-chat-border)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          background: 'var(--ac-chat-card-solid)',
          boxSizing: 'border-box',
        }}
      >
        {/* Lado Esquerdo: Contato ativo alinhado perfeitamente à esquerda */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1, textAlign: 'left' }}>
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <UserAvatar
              src={activePartnerPhoto}
              name={activeChatPartnerName}
              role={activeChatPartnerCargo || activeChatPartnerPerfil}
              size="sm"
            />
            <span className="ac-chat-presence" style={{ position: 'absolute', bottom: -2, right: -2 }} />
          </div>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <h4
              className="ac-chat-title"
              title={activeChatPartnerName}
              style={{
                margin: 0,
                fontSize: '0.88rem',
                fontWeight: 800,
                color: 'var(--ac-chat-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              {activeChatPartnerName}
            </h4>
            <p
              className="ac-chat-subtitle"
              style={{
                margin: '2px 0 0',
                fontSize: '0.68rem',
                fontWeight: 600,
                color: 'var(--ac-chat-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                textAlign: 'left',
              }}
            >
              <span>{getSingleRoleName(activeChatPartnerPerfil, activeChatPartnerCargo)}</span>
              {activeChatPartnerEscola && (
                <>
                  <span style={{ opacity: 0.5 }}>•</span>
                  <span title={activeChatPartnerEscola}>{activeChatPartnerEscola}</span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Lado Direito: Card do Usuário Logado encaixado perfeitamente na faixa */}
        {user && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 14px 6px 10px',
              borderRadius: 12,
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--ac-chat-border)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.20)',
              flexShrink: 0,
              maxWidth: 340,
            }}
            title={`Conectado como: ${user.nome} (${user.email || ''})`}
          >
            <UserAvatar
              src={(user as any).fotoUrl}
              name={user.nome}
              role={user.cargo || user.perfil}
              size="sm"
              style={{ width: 34, height: 34, flexShrink: 0 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <strong
                  style={{
                    fontSize: '0.80rem',
                    fontWeight: 750,
                    color: 'var(--ac-chat-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 150,
                  }}
                >
                  {user.nome}
                </strong>
                <span
                  style={{
                    fontSize: '0.58rem',
                    fontWeight: 800,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: 'rgba(147, 51, 234, 0.25)',
                    color: '#c084fc',
                    border: '1px solid rgba(147, 51, 234, 0.45)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {user.perfil}
                </span>
              </div>
              <span
                style={{
                  fontSize: '0.66rem',
                  color: 'var(--ac-chat-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 220,
                }}
              >
                {user.email || (user.cargo ? `Cargo: ${user.cargo}` : 'Online')}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSidebar = () => {
    return null; // Ocultado temporariamente por solicitação do usuário
    /*
    return (
      <aside className="ac-chat-sidebar ac-chat-scroll" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '13px 0', overflowY: 'auto', flexShrink: 0 }}>
        <button
          type="button"
          onClick={handleOpenContacts}
          title="Novo chat"
          className="ac-chat-avatar"
          style={{
            width: 44,
            height: 44,
            border: 0,
            cursor: 'pointer',
            '--avatar-from': '#8b5cf6',
            '--avatar-to': '#2454d8',
          } as React.CSSProperties}
        >
          <Plus size={19} />
        </button>

        <div style={{ width: '60%', height: 1, background: 'var(--ac-chat-border)' }} />

        {conversas.map((conversa) => {
          const isSelected = conversa.id === selectedContatoId && viewMode === 'chat';
          const visual = getSegmentVisual(conversa.perfil, conversa.cargo);

          return (
            <button
              key={conversa.id}
              type="button"
              onClick={() => handleSelectContact(conversa.id)}
              title={`${conversa.nome}\n${formatRole(conversa.perfil, conversa.cargo)}`}
              style={{ position: 'relative', border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}
            >
              <div
                className="ac-chat-avatar"
                style={{
                  '--avatar-from': visual.from,
                  '--avatar-to': visual.to,
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isSelected ? `0 0 0 3px ${visual.soft}, 0 12px 28px rgba(15,23,42,.20)` : undefined,
                } as React.CSSProperties}
              >
                {getInitials(conversa.nome)}
                {conversa.unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: -5, right: -6 }} className="ac-chat-unread">
                    {conversa.unreadCount > 99 ? '99+' : conversa.unreadCount}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </aside>
    );
    */
  };

  const renderStatusBar = () => {
    // Ocultado temporariamente a pedido do usuário
    return null;
  };

  const renderFilterBar = () => {
    return null; // Ocultado por estar consolidado no ChatToolbar
  };

  const renderTicketFlowBar = () => {
    // Ocultado temporariamente a pedido do usuário
    return null;
  };

  const renderOpsPanel = () => {
    if (viewMode !== 'chat' || !showOpsPanel) return null;

    return (
      <div className="ac-chat-ops-panel">
        <div className="ac-chat-ops-grid">
          <div className="ac-chat-ops-card">
            <p className="ac-chat-ops-title">Prioridade</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {PRIORITY_OPTIONS.map((priority) => (
                <button
                  key={priority.key}
                  type="button"
                  className={`ac-chat-status-chip is-button ${activePriority === priority.key ? '' : 'is-muted'}`}
                  style={{ '--status-color': priority.color } as React.CSSProperties}
                  onClick={() => updatePriority(priority.key)}
                >
                  <Flag size={11} /> {priority.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ac-chat-ops-card">
            <p className="ac-chat-ops-title">SLA</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SLA_PRESETS.map((preset) => (
                <button key={preset.label} type="button" className="ac-chat-action-btn" onClick={() => updateSla(preset.hours)}>
                  <Timer size={12} /> {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ac-chat-ops-card">
          <p className="ac-chat-ops-title">Nota interna</p>
          <textarea
            value={internalNote}
            onChange={(event) => setInternalNote(event.target.value)}
            placeholder="Nota visível somente para a equipe interna..."
            className="ac-chat-note-textarea"
          />
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 8 }}>
            <button type="button" className="ac-chat-action-btn is-success" onClick={saveInternalNote}>
              <StickyNote size={12} /> Salvar nota
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={transferConversation}>
              <UserPlus size={12} /> Transferir
            </button>
            <button type="button" className="ac-chat-action-btn is-danger" onClick={closeConversation}>
              <CheckCircle2 size={12} /> Fechar
            </button>
          </div>
        </div>

        <div className="ac-chat-ops-card">
          <p className="ac-chat-ops-title">Automação V7</p>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <button type="button" className="ac-chat-action-btn" onClick={classifyConversation} disabled={automationRunning}>
              <BrainCircuit size={12} /> Classificar
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={summarizeConversation} disabled={automationRunning}>
              <Zap size={12} /> Resumir
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={escalateConversation} disabled={automationRunning}>
              <Route size={12} /> Escalar
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={reopenConversation} disabled={automationRunning}>
              <RefreshCw size={12} /> Reabrir
            </button>
            <button type="button" className="ac-chat-action-btn is-success" onClick={runAutomations} disabled={automationRunning}>
              <Zap size={12} /> Rodar regras
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={suggestReply} disabled={copilotLoading}>
              <Bot size={12} /> Sugerir resposta
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={runTriage} disabled={copilotLoading}>
              <BrainCircuit size={12} /> Triagem
            </button>
            <button type="button" className="ac-chat-action-btn" onClick={searchKnowledge} disabled={copilotLoading}>
              <BookOpen size={12} /> Base
            </button>
          </div>
          {copilotSuggestion && (
            <div className="ac-chat-copilot-box" style={{ marginTop: 9 }}>
              <p className="ac-chat-copilot-title"><Bot size={12} /> Copiloto operacional</p>
              <p className="ac-chat-automation-text">{copilotSuggestion}</p>
            </div>
          )}
          {automationSummary && (
            <div className="ac-chat-automation-box" style={{ marginTop: 9 }}>
              <p className="ac-chat-automation-title"><BrainCircuit size={12} /> Resumo operacional</p>
              <p className="ac-chat-automation-text">{automationSummary}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderConversationList = () => {
    if (loadingConversas && conversas.length === 0) return <SkeletonList />;

    if (segmentedConversas.length === 0) {
      if (searchText.trim()) {
        return (
          <div className="ac-chat-empty">
            <div className="ac-chat-empty-icon"><Search size={24} /></div>
            <h4 style={{ margin: 0, fontSize: '0.84rem', fontWeight: 800, color: 'var(--ac-chat-text)' }}>
              Nenhuma conversa encontrada
            </h4>
            <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: 'var(--ac-chat-muted)' }}>
              Não encontramos conversas para "{searchText.trim()}".
            </p>
          </div>
        );
      }
      return (
        <div className="ac-chat-empty">
          <div className="ac-chat-empty-icon"><MessageSquare size={26} /></div>
          <h4 style={{ margin: 0, fontSize: '0.86rem', fontWeight: 800, color: 'var(--ac-chat-text)' }}>
            Nenhuma conversa iniciada
          </h4>
          <p style={{ margin: '6px 0 14px', fontSize: '0.72rem', color: 'var(--ac-chat-muted)', maxWidth: 270, lineHeight: 1.4 }}>
            Acesse a aba <strong>Contatos</strong> abaixo para escolher um membro da equipe, escola ou professor e iniciar um diálogo.
          </p>
          <button
            type="button"
            onClick={() => setViewMode('contatos')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--ac-chat-primary)',
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <Users size={14} /> Abrir catálogo de contatos
          </button>
        </div>
      );
    }

    // Helper para renderizar card de conversa na árvore ou lista
    const renderConversaTreeItem = (conversa: ConversaAtiva, indentLevel: number, contextLabel?: string | null) => {
      const isSelected = conversa.id === selectedContatoId;
      const isFavorite = favoriteIds.includes(conversa.id);
      const isPinned = pinnedContactIds.includes(conversa.id);
      const isMuted = !!mutedContactIds[conversa.id];
      const isBlocked = blockedContactIds.includes(conversa.id);
      const isMenuOpen = activeMenuContactId === conversa.id;
      const indentPadding = indentLevel === 1 ? 24 : indentLevel === 2 ? 36 : 12;
      const borderLeftMargin = indentLevel === 1 ? 14 : indentLevel === 2 ? 26 : 0;

      // Se existir contextLabel, usa ele; senão usa o cargo/perfil no mesmo formato padronizado
      const topLabel = contextLabel || getSingleRoleName(conversa.perfil, conversa.cargo);

      return (
        <div
          key={conversa.id}
          onClick={() => handleSelectContact(conversa.id)}
          className={`ac-chat-list-item ${isSelected ? 'is-selected' : ''}`}
          style={{
            padding: `9px 12px 9px ${indentPadding}px`,
            background: isSelected
              ? undefined
              : isPinned
              ? 'rgba(59, 130, 246, 0.08)'
              : undefined,
            borderLeft: isPinned
              ? '3px solid #3b82f6'
              : indentLevel > 0
              ? '2px solid rgba(255, 255, 255, 0.05)'
              : undefined,
            marginLeft: borderLeftMargin,
            position: 'relative',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <UserAvatar
            src={conversa.fotoUrl}
            name={conversa.nome}
            role={conversa.cargo || conversa.perfil}
            size="md"
            style={{ width: 44, height: 44, flexShrink: 0 }}
          />

          <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Linha 1: Nome do Contato à esquerda com Pins/Mute e Rótulo Contextual + 3 Pontinhos à direita */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, flex: 1 }}>
                <strong
                  style={{
                    fontSize: '0.84rem',
                    fontWeight: 750,
                    color: 'var(--ac-chat-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={conversa.nome}
                >
                  {conversa.nome}
                </strong>
                {isPinned && (
                  <span title="Conversa Fixada" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Pin size={12} style={{ color: '#38bdf8', transform: 'rotate(45deg)', flexShrink: 0 }} />
                  </span>
                )}
                {isMuted && (
                  <span title="Notificações Silenciadas" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <BellOff size={12} style={{ color: '#fbbf24', flexShrink: 0 }} />
                  </span>
                )}
                {isBlocked && (
                  <span title="Contato Bloqueado" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <ShieldAlert size={12} style={{ color: '#ef4444', flexShrink: 0 }} />
                  </span>
                )}
                {isFavorite && (
                  <span title="Favorito" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Star size={11} style={{ color: '#eab308', fill: '#eab308', flexShrink: 0 }} />
                  </span>
                )}
              </div>

              {/* Lado direito superior: Rótulo Contextual + Botão 3 pontinhos */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <span
                  style={{
                    fontSize: '0.58rem',
                    fontWeight: 600,
                    color: 'var(--ac-chat-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 100,
                    opacity: 0.85,
                  }}
                  title={topLabel}
                >
                  {topLabel}
                </span>

                <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    title="Mais opções"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenuContactId(isMenuOpen ? null : conversa.id);
                    }}
                    style={{
                      background: isMenuOpen ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                      border: 'none',
                      color: isMenuOpen ? 'var(--ac-chat-text)' : 'var(--ac-chat-muted)',
                      cursor: 'pointer',
                      padding: '2px',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 120ms ease, color 120ms ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ac-chat-text)'; }}
                    onMouseLeave={(e) => { if (!isMenuOpen) e.currentTarget.style.color = 'var(--ac-chat-muted)'; }}
                  >
                    <MoreVertical size={13} />
                  </button>

                  {/* Popup Menu Contextual */}
                  {isMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 4,
                        background: 'var(--ac-chat-card-solid)',
                        border: '1px solid var(--ac-chat-border)',
                        borderRadius: 10,
                        boxShadow: 'var(--ac-chat-shadow)',
                        zIndex: 999,
                        minWidth: 165,
                        padding: '4px 0',
                        backdropFilter: 'blur(12px)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          handleSelectContact(conversa.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: 'var(--ac-chat-text)',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <MessageSquare size={13} style={{ color: '#3b82f6' }} />
                        <span>Abrir Conversa</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          handleTogglePin(conversa.id, isPinned);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: 'var(--ac-chat-text)',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <Pin size={13} style={{ color: '#38bdf8' }} />
                        <span>{isPinned ? 'Desafixar do Topo' : 'Fixar no Topo'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          setMuteTargetContactId(conversa.id);
                          setShowMuteModal(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: 'var(--ac-chat-text)',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251, 191, 36, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <BellOff size={13} style={{ color: '#fbbf24' }} />
                        <span>{isMuted ? 'Reativar Notificações' : 'Silenciar Notificações'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          handleToggleBlock(conversa.id, isBlocked);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: isBlocked ? '#16a34a' : '#ef4444',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <ShieldAlert size={13} style={{ color: isBlocked ? '#16a34a' : '#ef4444' }} />
                        <span>{isBlocked ? 'Desbloquear Contato' : 'Bloquear Contato'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          toggleFavorite(conversa.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: 'var(--ac-chat-text)',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(234, 179, 8, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <Star size={13} style={{ color: isFavorite ? '#eab308' : 'var(--ac-chat-muted)', fill: isFavorite ? '#eab308' : 'none' }} />
                        <span>{isFavorite ? 'Desfavoritar' : 'Favoritar'}</span>
                      </button>

                      <div style={{ height: 1, background: 'var(--ac-chat-border)', margin: '4px 0' }} />

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          handleRemoveConversa(conversa.id, e);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <X size={13} style={{ color: '#ef4444' }} />
                        <span>Encerrar Atendimento</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Linha 2: Prévia da Conversa como no WhatsApp */}
            <div
              style={{
                fontSize: '0.73rem',
                color: conversa.unreadCount > 0 ? 'var(--ac-chat-text)' : 'var(--ac-chat-muted)',
                fontWeight: conversa.unreadCount > 0 ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
              title={conversa.ultimoTexto || 'Conversa iniciada'}
            >
              {conversa.ultimoTexto ? truncateText(conversa.ultimoTexto, 45) : 'Conversa iniciada'}
            </div>

            {/* Linha 3: Tempo Relativo à esquerda E Badge de Não Lidas à direita */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 1 }}>
              <span
                style={{
                  fontSize: '0.62rem',
                  color: 'var(--ac-chat-muted)',
                  opacity: 0.8,
                  fontWeight: 500,
                }}
              >
                {formatRelativeTime(conversa.atualizadoEm)}
              </span>

              {conversa.unreadCount > 0 && (
                <span
                  className="ac-chat-unread"
                  style={{
                    minWidth: 18,
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
                    boxShadow: '0 0 10px rgba(56, 189, 248, 0.5)',
                  }}
                >
                  {conversa.unreadCount > 99 ? '99+' : conversa.unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="ac-chat-scroll" style={{ flex: 1, overflowY: 'auto' }}>
        {segmentedConversas.map((conversa) =>
          renderConversaTreeItem(conversa, 0, getConversaContextLabel(conversa))
        )}
      </div>
    );
  };

  const renderContactsList = () => {
    if (loadingContatos) return <SkeletonList />;

    if (segmentedContatos.length === 0) {
      return (
        <div className="ac-chat-empty">
          <div className="ac-chat-empty-icon"><Search size={22} /></div>
          <h4 style={{ margin: 0, fontSize: '0.82rem', fontWeight: 950, color: 'var(--ac-chat-text)' }}>
            {searchText.trim() ? 'Nenhum contato encontrado' : 'Nenhum contato nesta visão'}
          </h4>
          <p style={{ margin: '6px 0 0', fontSize: '0.7rem', fontWeight: 650 }}>
            {searchText.trim() ? `Não encontramos contatos para "${searchText.trim()}".` : 'Troque o filtro ou limpe a busca.'}
          </p>
        </div>
      );
    }

    // Helper para renderizar item de contato com rótulo contextual/cargo padronizado no topo e menu de 3 pontinhos
    const renderContactTreeItem = (contato: ContatoHierarquico, indentLevel: number, contextLabel?: string | null) => {
      const isFavorite = favoriteIds.includes(contato.id);
      const isMenuOpen = activeMenuContactId === contato.id;
      const indentPadding = indentLevel === 1 ? 24 : indentLevel === 2 ? 36 : 12;
      const borderLeftMargin = indentLevel === 1 ? 14 : indentLevel === 2 ? 26 : 0;

      // Se existir contextLabel, usa ele; senão usa o cargo/perfil no mesmo formato padronizado
      const topLabel = contextLabel || getSingleRoleName(contato.perfil, contato.cargo);

      return (
        <div
          key={contato.id}
          onClick={() => handleSelectContact(contato.id)}
          className="ac-chat-list-item"
          style={{
            padding: `7px 12px 7px ${indentPadding}px`,
            borderLeft: indentLevel > 0 ? '2px solid rgba(255, 255, 255, 0.05)' : undefined,
            marginLeft: borderLeftMargin,
            position: 'relative',
          }}
        >
          <UserAvatar
            src={contato.fotoUrl}
            name={contato.nome}
            role={contato.cargo || contato.perfil}
            size="sm"
            style={{ flexShrink: 0 }}
          />

          <div style={{ minWidth: 0, flex: 1 }}>
            {/* Linha 1: Rótulo Padronizado (Contexto ou Cargo) à esquerda E Botão 3 pontinhos à direita */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 1 }}>
              <span
                style={{
                  fontSize: '0.56rem',
                  fontWeight: 500, // SEM NEGRITO
                  color: 'var(--ac-chat-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.01em',
                }}
                title={topLabel}
              >
                {topLabel}
              </span>

              {/* Lado direito superior: Estrela se favorito + Menu 3 pontinhos */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {isFavorite && (
                  <Star size={11} style={{ color: '#eab308', fill: '#eab308' }} />
                )}
                <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    title="Mais opções"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenuContactId(isMenuOpen ? null : contato.id);
                    }}
                    style={{
                      background: isMenuOpen ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                      border: 'none',
                      color: isMenuOpen ? 'var(--ac-chat-text)' : 'var(--ac-chat-muted)',
                      cursor: 'pointer',
                      padding: '2px',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 120ms ease, color 120ms ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ac-chat-text)'; }}
                    onMouseLeave={(e) => { if (!isMenuOpen) e.currentTarget.style.color = 'var(--ac-chat-muted)'; }}
                  >
                    <MoreVertical size={13} />
                  </button>

                  {/* Popup Menu Contextual */}
                  {isMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 4,
                        background: 'var(--ac-chat-card-solid)',
                        border: '1px solid var(--ac-chat-border)',
                        borderRadius: 8,
                        boxShadow: 'var(--ac-chat-shadow)',
                        zIndex: 999,
                        minWidth: 150,
                        padding: '4px 0',
                        backdropFilter: 'blur(12px)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          handleSelectContact(contato.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: 'var(--ac-chat-text)',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                         <MessageSquare size={13} style={{ color: '#3b82f6' }} />
                        <span>Abrir Conversa</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuContactId(null);
                          toggleFavorite(contato.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '7px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'none',
                          border: 'none',
                          color: 'var(--ac-chat-text)',
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(234, 179, 8, 0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <Star size={13} style={{ color: isFavorite ? '#eab308' : 'var(--ac-chat-muted)', fill: isFavorite ? '#eab308' : 'none' }} />
                        <span>{isFavorite ? 'Desfavoritar' : 'Favoritar'}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Linha 2: Nome do usuário */}
            <h4 className="ac-chat-name" title={contato.nome} style={{ margin: 0, fontSize: '0.78rem', fontWeight: 700 }}>
              {contato.nome}
            </h4>
          </div>
        </div>
      );
    };

    // Modo Agrupado com Acordeões Hierárquicos (quando não há filtro específico no dropdown)
    if (hierarchicalContactGroups && hierarchicalContactGroups.length > 0) {
      return (
        <div className="ac-chat-scroll" style={{ flex: 1, overflowY: 'auto' }}>
          {hierarchicalContactGroups.map((group) => {
            // Se for registro único:
            if (group.singleContact) {
              return renderContactTreeItem(group.singleContact, 0, group.singleContextLabel);
            }

            const GroupIcon = group.icon;
            const isSearching = Boolean(searchText.trim());
            const isGroupCollapsed = isSearching ? false : (collapsedGroups[group.id] !== undefined ? collapsedGroups[group.id] : true);

            return (
              <div key={group.id} style={{ borderBottom: '1px solid var(--ac-chat-border)' }}>
                {/* Linha Única do Grupo Principal (Nível 0) */}
                <div
                  onClick={() => toggleGroupCollapse(group.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 38,
                    padding: '0 12px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background 140ms ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ color: 'var(--ac-chat-muted)', display: 'flex', alignItems: 'center' }}>
                      {isGroupCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    </span>
                    <span style={{ color: group.color, display: 'flex', alignItems: 'center' }}>
                      <GroupIcon size={15} />
                    </span>
                    <span style={{
                      fontSize: '0.76rem',
                      fontWeight: 500, // SEM NEGRITO
                      color: 'var(--ac-chat-text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {group.title}
                    </span>
                  </div>

                  {/* Contador exibido SOMENTE quando colapsado */}
                  {isGroupCollapsed && (
                    <span style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: group.color,
                      background: group.bgBadge,
                      padding: '2px 8px',
                      borderRadius: 10,
                      flexShrink: 0,
                    }}>
                      {group.totalCount}
                    </span>
                  )}
                </div>

                {/* Conteúdo Expandido do Grupo Principal */}
                {!isGroupCollapsed && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {group.subGroups ? (
                      group.subGroups.map((sub) => {
                        // Se o subgrupo tiver apenas 1 contato: RENDERIZA DIRETO O CARD COM O SUBGRUPO EM CIMA!
                        if (sub.singleContact) {
                          return renderContactTreeItem(sub.singleContact, 1, sub.singleContextLabel);
                        }

                        const SubIcon = sub.icon;
                        const isSubCollapsed = isSearching ? false : (collapsedGroups[sub.id] !== undefined ? collapsedGroups[sub.id] : true);

                        return (
                          <div key={sub.id} style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}>
                            {/* Linha Única do Subgrupo (Nível 1) */}
                            <div
                              onClick={() => toggleGroupCollapse(sub.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                height: 32,
                                padding: '0 12px 0 20px',
                                borderLeft: '2px solid rgba(255, 255, 255, 0.08)',
                                marginLeft: 14,
                                background: 'rgba(255, 255, 255, 0.015)',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'background 140ms ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.015)'; }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                <span style={{ color: 'var(--ac-chat-muted)', display: 'flex', alignItems: 'center' }}>
                                  {isSubCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                </span>
                                <span style={{ color: sub.color, display: 'flex', alignItems: 'center' }}>
                                  <SubIcon size={13} />
                                </span>
                                <span style={{
                                  fontSize: '0.72rem',
                                  fontWeight: 500, // SEM NEGRITO
                                  color: 'var(--ac-chat-text)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {sub.title}
                                </span>
                              </div>

                              {/* Contador exibido SOMENTE quando colapsado */}
                              {isSubCollapsed && (
                                <span style={{
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  color: sub.color,
                                  background: sub.bgBadge,
                                  padding: '1px 6px',
                                  borderRadius: 8,
                                  flexShrink: 0,
                                }}>
                                  {sub.contacts.length}
                                </span>
                              )}
                            </div>

                            {/* Contatos do Subgrupo (Nível 2) */}
                            {!isSubCollapsed && (
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {sub.contacts.map((contato) => renderContactTreeItem(contato, 2))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      group.contacts?.map((contato) => renderContactTreeItem(contato, 1))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="ac-chat-scroll" style={{ flex: 1, overflowY: 'auto' }}>
        {segmentedContatos.map((contato) => renderContactTreeItem(contato, 0, contato.escola?.nome))}
      </div>
    );
  };

  const renderHistoricoList = () => {
    if (loadingHistorico && historicoConversas.length === 0) return <SkeletonList />;

    const encerrados = historicoConversas.filter((h) => !h.isBloqueado);
    const bloqueados = historicoConversas.filter((h) => h.isBloqueado || blockedContactIds.includes(h.id));

    const activeList = historicoSubTab === 'encerrados' ? encerrados : bloqueados;

    const filtered = activeList.filter((h) => {
      if (!historicoSearch.trim()) return true;
      const term = historicoSearch.toLowerCase();
      const nome = (h.nome || h.contato?.nome || '').toLowerCase();
      const motivo = (h.motivoFechamento || h.motivoBloqueio || h.resumoOperacional?.motivoFechamento || '').toLowerCase();
      const id = (h.id || '').toLowerCase();
      return nome.includes(term) || motivo.includes(term) || id.includes(term);
    });

    return (
      <div className="ac-chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Abas Superiores do Histórico: Encerrados vs Bloqueados */}
        <div style={{ display: 'flex', gap: 6, paddingBottom: 6, borderBottom: '1px solid var(--ac-chat-border)' }}>
          <button
            type="button"
            onClick={() => setHistoricoSubTab('encerrados')}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 8,
              fontSize: '0.72rem',
              fontWeight: historicoSubTab === 'encerrados' ? 800 : 600,
              border: historicoSubTab === 'encerrados' ? '1px solid #2563eb' : '1px solid transparent',
              background: historicoSubTab === 'encerrados' ? 'rgba(37, 99, 235, 0.12)' : 'var(--ac-chat-soft)',
              color: historicoSubTab === 'encerrados' ? '#2563eb' : 'var(--ac-chat-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 140ms ease',
            }}
          >
            <Clock3 size={13} />
            <span>Encerrados ({encerrados.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setHistoricoSubTab('bloqueados')}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 8,
              fontSize: '0.72rem',
              fontWeight: historicoSubTab === 'bloqueados' ? 800 : 600,
              border: historicoSubTab === 'bloqueados' ? '1px solid #ef4444' : '1px solid transparent',
              background: historicoSubTab === 'bloqueados' ? 'rgba(239, 68, 68, 0.12)' : 'var(--ac-chat-soft)',
              color: historicoSubTab === 'bloqueados' ? '#ef4444' : 'var(--ac-chat-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 140ms ease',
            }}
          >
            <ShieldAlert size={13} />
            <span>Bloqueados ({bloqueados.length})</span>
          </button>
        </div>

        {filtered.length === 0 ? (
          <div className="ac-chat-empty" style={{ minHeight: 200 }}>
            <div className="ac-chat-empty-icon">
              {historicoSubTab === 'encerrados' ? <Clock3 size={24} /> : <ShieldCheck size={24} />}
            </div>
            <h4 style={{ margin: 0, fontSize: '0.84rem', fontWeight: 800, color: 'var(--ac-chat-text)' }}>
              {historicoSubTab === 'encerrados' ? 'Nenhum atendimento encerrado' : 'Nenhum contato bloqueado'}
            </h4>
            <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: 'var(--ac-chat-muted)', textAlign: 'center' }}>
              {historicoSubTab === 'encerrados'
                ? 'Conversas resolvidas e encerradas serão arquivadas aqui com data e motivo de fechamento.'
                : 'Contatos bloqueados ficam arquivados nesta lista e você pode desbloqueá-los a qualquer momento.'}
            </p>
          </div>
        ) : (
          filtered.map((item) => {
            const contactId = item.contatoId || item.id;
            const contactName = item.nome || item.contato?.nome || 'Contato';
            const roleName = item.cargo || item.perfil || item.contato?.cargo || item.contato?.perfil || 'Membro da Equipe';
            const foto = item.fotoUrl || item.contato?.fotoUrl;
            const motivo = item.motivoFechamento || item.motivoBloqueio || item.resumoOperacional?.motivoFechamento || (item.isBloqueado ? 'Contato bloqueado' : 'Resolvido pelo operador');
            const closedAt = item.fechadoEm || item.atualizadoEm;

            return (
              <div
                key={item.id}
                className="ac-chat-historico-card"
                style={{ position: 'relative' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserAvatar
                      src={foto}
                      name={contactName}
                      role={roleName}
                      size="sm"
                    />
                    <div>
                      <strong style={{ fontSize: '0.82rem', color: 'var(--ac-chat-text)' }}>{contactName}</strong>
                      <div style={{ fontSize: '0.64rem', color: 'var(--ac-chat-muted)' }}>
                        {formatRole(item.perfil || item.contato?.perfil, item.cargo || item.contato?.cargo)}
                        {item.escolaNome ? ` • ${item.escolaNome}` : ''}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.62rem', color: 'var(--ac-chat-muted)', opacity: 0.85 }}>
                    {formatRelativeTime(closedAt)}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.70rem', color: 'var(--ac-chat-muted)', background: 'rgba(255, 255, 255, 0.04)', padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(255, 255, 255, 0.08)', flex: 1, minWidth: 0 }}>
                    {item.isBloqueado ? (
                      <ShieldAlert size={13} style={{ color: '#ef4444', flexShrink: 0 }} />
                    ) : (
                      <CheckCircle2 size={13} style={{ color: '#10b981', flexShrink: 0 }} />
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong style={{ color: '#f8fafc' }}>{item.isBloqueado ? 'Status:' : 'Motivo:'}</strong> {motivo}
                    </span>
                  </div>

                  {item.isBloqueado ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleBlock(contactId, true);
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: 'none',
                        background: '#16a34a',
                        color: '#ffffff',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        flexShrink: 0,
                        boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)',
                      }}
                    >
                      <ShieldCheck size={13} />
                      <span>Desbloquear</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (contactId) {
                          handleSelectContact(contactId);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--ac-chat-border)',
                        background: 'var(--ac-chat-soft)',
                        color: 'var(--ac-chat-text)',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        flexShrink: 0,
                      }}
                    >
                      <MessageSquare size={13} />
                      <span>Ver Chat</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderAnexosHub = () => {
    if (loadingAnexos && anexosList.length === 0) return <SkeletonList />;

    const tipos = [
      { id: 'TODOS', label: 'Todas' },
      { id: 'DOCUMENTO', label: 'Docs' },
      { id: 'IMAGEM', label: 'Foto' },
      { id: 'VIDEO', label: 'Video' },
      { id: 'AUDIO', label: 'Audio' },
    ];

    const filtered = anexosList
      .filter((a) => {
        if (anexosFilterTipo !== 'TODOS') {
          if (anexosFilterTipo === 'IMAGEM' && a.tipo !== 'IMAGEM') return false;
          if (anexosFilterTipo === 'VIDEO' && a.tipo !== 'VIDEO') return false;
          if (anexosFilterTipo === 'DOCUMENTO' && a.tipo !== 'DOCUMENTO') return false;
          if (anexosFilterTipo === 'AUDIO' && a.tipo !== 'AUDIO') return false;
          if (anexosFilterTipo === 'CONTATO' && a.tipo !== 'CONTATO') return false;
        }
        if (anexosSearch.trim()) {
          const term = anexosSearch.toLowerCase();
          return (
            (a.arquivoNome && a.arquivoNome.toLowerCase().includes(term)) ||
            (a.texto && a.texto.toLowerCase().includes(term)) ||
            (a.remetente?.nome && a.remetente.nome.toLowerCase().includes(term))
          );
        }
        return true;
      })
      .sort((a, b) => {
        // Ordenação estrita: Mais recente em cima, mais antigo em baixo
        const dateA = safeDate(a.criadoEm || a.atualizadoEm)?.getTime() || 0;
        const dateB = safeDate(b.criadoEm || b.atualizadoEm)?.getTime() || 0;
        return dateB - dateA;
      });

    return (
      <div className="ac-chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Abas de filtro de tipo de anexo estilo WhatsApp */}
        <div
          className="ac-chat-chip-row"
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: 2,
          }}
        >
          {tipos.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setAnexosFilterTipo(t.id);
                loadAnexos(t.id);
              }}
              style={{
                padding: '4px 10px',
                borderRadius: 14,
                fontSize: '0.68rem',
                fontWeight: 700,
                border: anexosFilterTipo === t.id ? '1px solid #2563eb' : '1px solid var(--ac-chat-border)',
                background: anexosFilterTipo === t.id ? 'rgba(37, 99, 235, 0.12)' : 'var(--ac-chat-soft)',
                color: anexosFilterTipo === t.id ? '#2563eb' : 'var(--ac-chat-muted)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 120ms ease',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="ac-chat-empty" style={{ minHeight: 220 }}>
            <div className="ac-chat-empty-icon"><Paperclip size={24} /></div>
            <h4 style={{ margin: 0, fontSize: '0.84rem', fontWeight: 800, color: 'var(--ac-chat-text)' }}>
              Nenhum anexo encontrado
            </h4>
            <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: 'var(--ac-chat-muted)', textAlign: 'center' }}>
              Mídias, documentos e contatos compartilhados aparecerão catalogados aqui.
            </p>
          </div>
        ) : anexosFilterTipo === 'IMAGEM' || anexosFilterTipo === 'VIDEO' ? (
          /* Grid 3x3 de Fotos e Vídeos estilo Galeria WhatsApp */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {filtered.map((item) => {
              const fileName = item.arquivoNome || item.texto || 'Mídia';
              const isVideo = item.tipo === 'VIDEO';

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (item.arquivoUrl) {
                      setLightboxImage({ src: item.arquivoUrl, title: fileName });
                    }
                  }}
                  style={{
                    position: 'relative',
                    aspectRatio: '1 / 1',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: 'var(--ac-chat-soft)',
                    border: '1px solid var(--ac-chat-border)',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  {item.arquivoUrl ? (
                    <img
                      src={item.arquivoUrl}
                      alt={fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 150ms ease' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImagePlus size={20} style={{ color: 'var(--ac-chat-muted)' }} />
                    </div>
                  )}

                  {isVideo && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                        padding: '2px 4px',
                        borderRadius: 4,
                        background: 'rgba(0, 0, 0, 0.65)',
                        color: '#fff',
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Video size={10} />
                      <span>VÍDEO</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Lista compacta de Mídias e Documentos estilo WhatsApp */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map((item) => {
              const fileName = item.arquivoNome || item.texto || 'Anexo';
              const isImage = item.tipo === 'IMAGEM';
              const isVideo = item.tipo === 'VIDEO';
              const isAudio = item.tipo === 'AUDIO';
              const isContact = item.tipo === 'CONTATO';

              return (
                <div
                  key={item.id}
                  className="ac-chat-anexo-card"
                  onClick={() => {
                    if (isImage && item.arquivoUrl) {
                      setLightboxImage({ src: item.arquivoUrl, title: fileName });
                    } else if (item.arquivoUrl) {
                      window.open(item.arquivoUrl, '_blank');
                    }
                  }}
                >
                  {/* Prévia / Ícone */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: 'var(--ac-chat-soft)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isImage && item.arquivoUrl ? (
                      <img src={item.arquivoUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : isVideo ? (
                      <Video size={18} style={{ color: '#818cf8' }} />
                    ) : isAudio ? (
                      <Mic size={18} style={{ color: '#38bdf8' }} />
                    ) : isContact ? (
                      <Users size={18} style={{ color: '#10b981' }} />
                    ) : (
                      <FileText size={18} style={{ color: '#f59e0b' }} />
                    )}
                  </div>

                  {/* Detalhes */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.76rem',
                        fontWeight: 650,
                        color: 'var(--ac-chat-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={fileName}
                    >
                      {fileName}
                    </div>
                    <div style={{ display: 'flex', gap: 6, fontSize: '0.62rem', color: 'var(--ac-chat-muted)', marginTop: 2 }}>
                      <span>{item.remetente?.nome || 'Contato'}</span>
                      {item.arquivoTamanho && <span>• {Math.round(item.arquivoTamanho / 1024)} KB</span>}
                      <span>• {formatRelativeTime(item.criadoEm)}</span>
                    </div>
                  </div>

                  {/* Ação */}
                  {item.arquivoUrl && (
                    <a
                      href={item.arquivoUrl}
                      download={fileName}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: 6,
                        borderRadius: 8,
                        color: 'var(--ac-chat-muted)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Baixar arquivo"
                    >
                      <Download size={14} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMessageContent = (message: MensagemInterna) => {
    const tipo = message.tipo || 'TEXTO';
    const hasFile = Boolean(message.arquivoUrl);
    const meta = (message.metadata as any) || {};
    const isDeleted = meta.isRemovido || message.texto === '🚫 Esta mensagem foi apagada';

    if (isDeleted) {
      return (
        <div className="ac-chat-deleted-msg" style={{ display: 'flex', alignItems: 'center', gap: 6, fontStyle: 'italic', color: 'var(--ac-chat-muted)', opacity: 0.8, fontSize: '0.76rem' }}>
          <span>🚫 Esta mensagem foi apagada</span>
        </div>
      );
    }

    // Se o texto for igual ao nome do arquivo ou vazio, não duplica
    const hasDistinctCaption =
      message.texto &&
      message.texto !== message.arquivoNome &&
      !message.texto.startsWith('[AC_CONTACT_CARD]');

    return (
      <div className="ac-chat-media-preview" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Bloco de citação se for resposta */}
        {meta.replyTo && (
          <div className="ac-chat-quote-bubble">
            <strong>{meta.replyTo.remetenteNome}:</strong>
            <span>{truncateText(meta.replyTo.texto, 60)}</span>
          </div>
        )}

        {tipo !== 'TEXTO' && (
          <span className="ac-chat-message-type">
            {tipo === 'IMAGEM' && <ImagePlus size={11} />}
            {tipo === 'VIDEO' && <Video size={11} />}
            {tipo === 'AUDIO' && <Mic size={11} />}
            {tipo === 'DOCUMENTO' && <FileText size={11} />}
            {tipo === 'FEEDBACK' && <Bug size={11} />}
            {tipo === 'COMUNICADO' && <Sparkles size={11} />}
            {tipo}
          </span>
        )}

        {hasFile && tipo === 'IMAGEM' && (
          <div
            style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', maxWidth: 280 }}
            onClick={() => setLightboxImage({ src: message.arquivoUrl || '', title: message.arquivoNome || 'Imagem' })}
          >
            <img
              src={message.arquivoUrl || ''}
              alt={message.arquivoNome || 'Imagem enviada'}
              loading="lazy"
              style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block', borderRadius: 10 }}
            />
            <div style={{ position: 'absolute', right: 6, bottom: 6, background: 'rgba(0,0,0,0.65)', borderRadius: 6, padding: '3px 6px', color: '#fff', fontSize: '0.62rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Eye size={10} /> Ampliar
            </div>
          </div>
        )}

        {hasFile && tipo === 'VIDEO' && (
          <video
            src={message.arquivoUrl || ''}
            controls
            style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 10, background: '#000' }}
          />
        )}

        {hasFile && tipo === 'AUDIO' && (
          <audio
            src={message.arquivoUrl || ''}
            controls
            style={{ width: '100%', minWidth: 220, marginTop: 2 }}
          />
        )}

        {hasFile && (tipo === 'DOCUMENTO' || tipo === 'FEEDBACK') && (
          <a
            className="ac-chat-file-card"
            href={message.arquivoUrl || '#'}
            target="_blank"
            rel="noreferrer"
            download={message.arquivoNome || true}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 10,
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--ac-chat-border)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <div style={{ flexShrink: 0 }}>
              {getFileIcon(message.arquivoNome, tipo)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ac-chat-text)' }}>
                {message.arquivoNome || 'Abrir documento'}
              </div>
              <div style={{ fontSize: '0.64rem', color: 'var(--ac-chat-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{formatFileSize(message.arquivoTamanho)}</span>
                <span>•</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--ac-chat-blue)' }}>
                  <Download size={10} /> Baixar
                </span>
              </div>
            </div>
          </a>
        )}

        {message.texto && message.texto.startsWith('[AC_CONTACT_CARD]') ? (
          renderAttachedContactCard(message.texto)
        ) : (
          hasDistinctCaption && <span style={{ fontSize: '0.80rem', wordBreak: 'break-word' }}>{message.texto}</span>
        )}

        {!hasDistinctCaption && !hasFile && !message.texto?.startsWith('[AC_CONTACT_CARD]') && (
          <span>{message.texto || 'Mensagem sem conteúdo.'}</span>
        )}
      </div>
    );
  };

  const renderChatMessages = () => (
    <div className="ac-chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px 16px', display: 'flex', flexDirection: 'column', gap: 12, background: 'transparent' }}>
      {/* Barra de Ações em Lote quando no Modo de Seleção Múltipla */}
      {isSelectionMode && (
        <div className="ac-chat-batch-bar">
          <div className="ac-chat-batch-count">
            <CheckSquare size={14} />
            <span>{selectedMessageIds.length} selecionada(s)</span>
          </div>
          <div className="ac-chat-batch-actions">
            <button
              type="button"
              className="ac-chat-batch-btn"
              onClick={() => handleStartForward(selectedMessageIds)}
              disabled={!selectedMessageIds.length}
              title="Encaminhar selecionadas"
            >
              <Forward size={13} /> Encaminhar
            </button>
            <button
              type="button"
              className="ac-chat-batch-btn is-danger"
              onClick={() => handleStartDelete(selectedMessageIds)}
              disabled={!selectedMessageIds.length}
              title="Apagar selecionadas"
            >
              <Trash2 size={13} /> Apagar
            </button>
            <button
              type="button"
              className="ac-chat-batch-btn"
              onClick={() => {
                setIsSelectionMode(false);
                setSelectedMessageIds([]);
              }}
            >
              <X size={13} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {loadingMessages && messages.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 34 }}>
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--ac-chat-blue)' }} />
        </div>
      ) : messages.length > 0 ? (
        messageBlocks.map((block) => (
          <React.Fragment key={block.day}>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}>
              <span style={{
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: 999,
                padding: '4px 11px',
                background: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '0.60rem',
                fontWeight: 800,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              }}>
                {block.day}
              </span>
            </div>

            {block.messages.map((message) => {
              const isMine = message.remetenteId === user?.id;
              const senderPhoto = isMine ? user?.fotoUrl : (message.remetente?.fotoUrl || activePartnerPhoto);
              const senderName = isMine ? (user?.nome || 'Você') : (message.remetente?.nome || activeChatPartnerName);
              const senderRole = isMine ? (user?.cargo || user?.perfil) : (message.remetente?.perfil);
              const meta = (message.metadata as any) || {};
              const isDeleted = meta.isRemovido || message.texto === '🚫 Esta mensagem foi apagada';
              const isMsgSelected = selectedMessageIds.includes(message.id);

              return (
                <div
                  key={message.id}
                  className={`ac-chat-message-row ${isMine ? 'mine' : 'theirs'} ${isMsgSelected ? 'is-selected' : ''}`}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 7,
                    maxWidth: '100%',
                    flexDirection: isMine ? 'row-reverse' : 'row',
                    alignSelf: isMine ? 'flex-end' : 'flex-start',
                  }}>
                    {isSelectionMode && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMessageIds((prev) =>
                            prev.includes(message.id) ? prev.filter((id) => id !== message.id) : [...prev, message.id]
                          );
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isMsgSelected ? '#38bdf8' : 'rgba(255,255,255,0.4)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: 4,
                          flexShrink: 0,
                          alignSelf: 'center',
                        }}
                      >
                        {isMsgSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                      </button>
                    )}

                    <UserAvatar
                      src={senderPhoto}
                      name={senderName}
                      role={senderRole}
                      size={26}
                      style={{ flexShrink: 0, marginBottom: 2 }}
                    />
                    <div className={`ac-chat-bubble ${isMine ? 'mine' : 'theirs'} ${message.pending ? 'pending' : ''} ${message.error ? 'error' : ''}`}>
                      {renderMessageContent(message)}
                      {!isSelectionMode && !isDeleted && (
                        <div className="ac-chat-bubble-tools">
                          <button type="button" className="ac-chat-mini-tool" title="Copiar texto" onClick={() => handleCopyMessage(message.texto)}>
                            <Copy size={12} />
                          </button>
                          <button type="button" className="ac-chat-mini-tool" title="Responder" onClick={() => handleReplyMessage(message)}>
                            <CornerUpLeft size={12} />
                          </button>
                          <button type="button" className="ac-chat-mini-tool" title="Encaminhar" onClick={() => handleStartForward([message.id])}>
                            <Forward size={12} />
                          </button>
                          <button
                            type="button"
                            className="ac-chat-mini-tool"
                            title="Selecionar"
                            onClick={() => {
                              setIsSelectionMode(true);
                              setSelectedMessageIds([message.id]);
                            }}
                          >
                            <CheckSquare size={12} />
                          </button>
                          {isMine && (
                            <button type="button" className="ac-chat-mini-tool" title="Apagar mensagem" onClick={() => handleStartDelete([message.id])}>
                              <Trash2 size={12} style={{ color: '#f87171' }} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="ac-chat-meta" style={{ marginRight: isMine ? 33 : 0, marginLeft: !isMine ? 33 : 0 }}>
                    {formatTime(message.criadoEm)}
                    {isMine && !message.pending && !message.error && message.lidaEm ? '· lida' : ''}
                    {isMine && !message.pending && !message.error && !message.lidaEm && message.entregueEm ? '· entregue' : ''}
                    {isMine && !message.pending && !message.error && !message.lidaEm && !message.entregueEm ? '· enviada' : ''}
                    {message.pending ? '· enviando...' : ''}
                    {message.error ? '· não enviada' : ''}
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        ))
      ) : (
        <div className="ac-chat-empty" style={{ margin: 'auto' }}>
          <div className="ac-chat-empty-icon"><Sparkles size={23} /></div>
          <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 950, color: 'var(--ac-chat-text)' }}>Conversa pronta para começar</h4>
          <p style={{ margin: '6px 0 0', fontSize: '0.7rem', fontWeight: 650 }}>Envie uma mensagem, peça evidência, vídeo ou print.</p>
        </div>
      )}
      {activeIsTyping && (
        <div className="ac-chat-message-row theirs">
          <div className="ac-chat-typing" title="Digitando">
            <span />
            <span />
            <span />
          </div>
          <span className="ac-chat-meta">digitando...</span>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );

  const renderComposer = () => (
    <div className="ac-chat-composer">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,application/*,text/*"
        onChange={handleSelectedFile}
        style={{ display: 'none' }}
      />

      {/* Banner de Resposta com Citação */}
      {replyingToMessage && (
        <div className="ac-chat-composer-quote">
          <div className="ac-chat-quote-bar" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ color: '#38bdf8', fontSize: '0.74rem' }}>
              Respondendo a {replyingToMessage.remetenteNome}:
            </strong>
            <div style={{ fontSize: '0.70rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {replyingToMessage.texto}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setReplyingToMessage(null)}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2 }}
            title="Cancelar resposta"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {uploadingMedia && (
        <div className="ac-chat-chip-row">
          <span className="ac-chat-uploading">
            <Loader2 size={12} className="animate-spin" /> Enviando mídia...
          </span>
        </div>
      )}

      {/* Barra de Preview de Anexo Pendente (com opção de remoção antes de enviar) */}
      {pendingAttachment && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '8px 12px',
            marginBottom: 8,
            borderRadius: 12,
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--ac-chat-border)',
            animation: 'fadeIn 150ms ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            {pendingAttachment.tipo === 'IMAGEM' && pendingAttachment.previewUrl ? (
              <img
                src={pendingAttachment.previewUrl}
                alt="Preview"
                style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--ac-chat-border)', flexShrink: 0 }}
              />
            ) : (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: 'rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {getFileIcon(pendingAttachment.nome, pendingAttachment.tipo)}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 650, color: 'var(--ac-chat-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pendingAttachment.nome}>
                {pendingAttachment.nome}
              </div>
              <div style={{ fontSize: '0.63rem', color: 'var(--ac-chat-muted)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 1, flexWrap: 'wrap' }}>
                <span>{formatFileSize(pendingAttachment.tamanho)}</span>
                <span>•</span>
                <span style={{ color: '#10b981', fontWeight: 600 }}>Pronto para envio</span>
                {!uploadQuota.isUnlimited && (
                  <>
                    <span>•</span>
                    <span style={{ opacity: 0.85 }}>Restará: {Math.max(0, uploadQuota.remainingMb - Number((pendingAttachment.tamanho / (1024 * 1024)).toFixed(2))).toFixed(2)} MB hoje</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            title="Remover anexo"
            onClick={() => {
              if (pendingAttachment.previewUrl) {
                try { URL.revokeObjectURL(pendingAttachment.previewUrl); } catch {}
              }
              setPendingAttachment(null);
            }}
            style={{
              background: 'rgba(239, 68, 68, 0.12)',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer',
              width: 26,
              height: 26,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background 120ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)'; }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {showMediaTray && (
        <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6, animation: 'acBubbleIn 150ms ease both' }}>
          <div className="ac-chat-media-tray" style={{ marginBottom: 0 }}>
            {ATTACH_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  type="button"
                  className="ac-chat-media-option"
                  onClick={() => handleAttachAction(action.key)}
                >
                  <div
                    className="ac-chat-media-option-icon"
                    style={{ background: action.bg, color: action.color, boxShadow: action.shadow }}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="ac-chat-media-option-text">
                    <div className="ac-chat-media-option-title">{action.label}</div>
                    <div className="ac-chat-media-option-sub">{action.sublabel}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Linha de Cota Disponível Abaixo dos Botões */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px',
              borderRadius: 12,
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              fontSize: '0.67rem',
              color: '#ffffff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={13} style={{ color: uploadQuota.isUnlimited ? '#c084fc' : '#34d399', filter: 'drop-shadow(0 0 4px currentColor)' }} />
              {uploadQuota.isUnlimited ? (
                <span>Cota de Upload: <strong style={{ color: '#c084fc' }}>Ilimitada (Master)</strong> · Até {uploadQuota.maxFileSizeMb}MB/anexo</span>
              ) : (
                <span>
                  Disponível hoje: <strong style={{ color: '#34d399' }}>{uploadQuota.remainingMb} MB</strong> de {uploadQuota.dailyUploadLimitMb} MB
                </span>
              )}
            </div>
            {!uploadQuota.isUnlimited && (
              <span style={{ fontSize: '0.62rem', color: 'rgba(255, 255, 255, 0.65)' }}>
                Máx: {uploadQuota.maxFileSizeMb}MB por anexo
              </span>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <button
          type="button"
          className="ac-chat-toolbar-btn"
          title="Anexar foto, vídeo, áudio, documento ou contato"
          onClick={() => setShowMediaTray((prev) => !prev)}
          disabled={uploadingMedia}
          style={{ width: 42, height: 42, borderRadius: 14, opacity: uploadingMedia ? .55 : 1 }}
        >
          {uploadingMedia ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
        </button>

        <textarea
          ref={composerRef}
          value={draftMessage}
          onChange={(e) => handleDraftChange(e.target.value)}
          onKeyDown={handleComposerKeyDown}
          onPaste={handleComposerPaste}
          placeholder={pendingAttachment ? 'Adicione uma legenda ou pressione Enter para enviar...' : (draftType === 'TEXTO' ? 'Digite sua mensagem... Enter envia · Shift+Enter quebra linha' : `Digite o conteúdo de ${draftType.toLowerCase()}...`)}
          className="ac-chat-textarea"
          rows={2}
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={(!draftMessage.trim() && !pendingAttachment) || sending}
          className="ac-chat-send-btn"
          title="Enviar mensagem"
        >
          {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
        </button>
      </div>
    </div>
  );

  const isMobileScreen = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveDocked = isDocked && !isMobileScreen && !embedded;

  const containerStyle: React.CSSProperties = isStandalone
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        borderRadius: 0,
        border: 0,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--ac-chat-card-solid)',
        zIndex: 9999,
      }
    : embedded
    ? {
        position: 'relative',
        height: '100%',
        width: '100%',
        maxWidth: 1400,
        margin: '0 auto',
        borderRadius: isMobileScreen ? 0 : 20,
        border: isMobileScreen ? 0 : '1px solid var(--ac-chat-border, rgba(255,255,255,0.12))',
        boxShadow: isMobileScreen ? 'none' : '0 18px 45px rgba(15, 23, 42, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--ac-chat-bg, #ffffff)',
        zIndex: 1,
      }
    : effectiveDocked
    ? {
        position: 'relative',
        height: '100vh',
        width: DOCK_WIDTH,
        borderLeft: '1px solid var(--ac-chat-border)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 40,
        boxShadow: '-12px 0 34px rgba(15, 23, 42, 0.10)',
      }
    : isMobileScreen
      ? {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100dvh',
          maxHeight: '100dvh',
          borderRadius: 0,
          border: 0,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 99999,
        }
      : {
          position: 'fixed',
          ...(isMaximized && !isMinimized
            ? { left: 18, top: 18, right: 'auto', bottom: 'auto' }
            : dragPos
              ? { left: dragPos.x, top: dragPos.y, right: 'auto', bottom: 'auto' }
              : { right: 24, bottom: 90 }),
          width: isMinimized ? 292 : isMaximized ? 'calc(100vw - 36px)' : DEFAULT_FLOAT_WIDTH,
          height: isMinimized ? 'auto' : isMaximized ? 'calc(100vh - 36px)' : DEFAULT_FLOAT_HEIGHT,
          maxHeight: isMinimized ? 'auto' : isMaximized ? 'none' : 'calc(100vh - 118px)',
          borderRadius: isMinimized ? 16 : isMaximized ? 18 : 'var(--ac-chat-radius)',
          border: isMinimized ? 0 : '1px solid var(--ac-chat-border)',
          boxShadow: isMinimized ? '0 14px 34px rgba(37, 84, 216, 0.28)' : isMaximized ? '0 0 0 9999px rgba(2, 6, 23, 0.42), var(--ac-chat-shadow)' : 'var(--ac-chat-shadow)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
          transition: isDragging.current ? 'none' : 'width 220ms ease, height 220ms ease, border-radius 220ms ease, box-shadow 220ms ease, opacity 160ms ease',
        };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      data-ac-chat-theme={themePreset}
      className={`ac-chat-shell ${themeMode === 'dark' ? 'ac-chat-theme-dark' : 'ac-chat-theme-light'} ${isDragging.current ? 'ac-chat-dragging' : ''}`}
    >
      {isStandalone && windowWidth >= 680 ? (
        /* ═══ MODO SPLIT-VIEW (JANELA EXTERNA / 2 COLUNAS ESTILO WHATSAPP) ═══ */
        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {/* Coluna Esquerda: Cabeçalho, Busca, Lista de Conversas/Contatos e Abas em Baixo */}
          <aside
            style={{
              width: 390,
              minWidth: 340,
              maxWidth: 440,
              borderRight: '1px solid var(--ac-chat-border)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'var(--ac-chat-card-solid)',
            }}
          >
            {renderHeader()}

            <ChatToolbar
              searchText={
                viewMode === 'anexos'
                  ? anexosSearch
                  : viewMode === 'historico'
                  ? historicoSearch
                  : searchText
              }
              onSearchChange={(val) => {
                if (viewMode === 'anexos') setAnexosSearch(val);
                else if (viewMode === 'historico') setHistoricoSearch(val);
                else setSearchText(val);
              }}
              searchPlaceholder={
                viewMode === 'contatos'
                  ? 'Buscar no catálogo de contatos...'
                  : viewMode === 'historico'
                  ? 'Pesquisar histórico por nome ou motivo...'
                  : viewMode === 'anexos'
                  ? 'Buscar fotos, vídeos, documentos...'
                  : 'Pesquisar ou começar uma nova conversa...'
              }
              showFilters={viewMode === 'conversas'}
              activeGroup={activeGroup}
              onGroupChange={(group) => setActiveGroup(group)}
              activeSubFilter={activeSubFilter}
              onSubFilterChange={(sub) => setActiveSubFilter(sub)}
              onlyUnread={onlyUnread}
              onOnlyUnreadChange={() => setOnlyUnread((prev) => !prev)}
              onlyFavorites={onlyFavorites}
              onOnlyFavoritesChange={() => setOnlyFavorites((prev) => !prev)}
              onOpenContacts={() => setViewMode('contatos')}
            />

            {renderStatusBar()}
            {renderFilterBar()}
            {renderTicketFlowBar()}
            {renderOpsPanel()}

            <section
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflowY: 'auto',
              }}
            >
              {viewMode === 'contatos'
                ? renderContactsList()
                : viewMode === 'historico'
                ? renderHistoricoList()
                : viewMode === 'anexos'
                ? renderAnexosHub()
                : renderConversationList()}
            </section>

            {/* Abas no Rodapé da Coluna Esquerda */}
            <div className="ac-chat-bottom-tabs">
              <button
                type="button"
                onClick={() => setViewMode('conversas')}
                className={`ac-chat-bottom-tab-btn ${viewMode === 'conversas' || viewMode === 'chat' ? 'is-active' : ''}`}
              >
                <MessageSquare size={17} />
                <span>Conversas</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('contatos')}
                className={`ac-chat-bottom-tab-btn ${viewMode === 'contatos' ? 'is-active' : ''}`}
              >
                <Users size={17} />
                <span>Contatos</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setViewMode('historico');
                  loadHistorico();
                }}
                className={`ac-chat-bottom-tab-btn ${viewMode === 'historico' ? 'is-active' : ''}`}
              >
                <Clock3 size={17} />
                <span>Histórico</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setViewMode('anexos');
                  loadAnexos(anexosFilterTipo);
                }}
                className={`ac-chat-bottom-tab-btn ${viewMode === 'anexos' ? 'is-active' : ''}`}
              >
                <Paperclip size={17} />
                <span>Anexos</span>
              </button>
            </div>
          </aside>

          {/* Coluna Direita: Conversa Ativa com Mensagens e Composer ou Tela Vazia Elegante */}
          <main
            className="ac-chat-panel-bg"
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              position: 'relative',
            }}
          >
            {selectedContatoId ? (
              <>
                {renderRightHeader()}
                {renderChatMessages()}
                {renderComposer()}
              </>
            ) : (
              <>
                {/* Faixa Superior no Modo Vazio com o Card do Usuário Logado à Direita */}
                <div
                  className="ac-chat-header"
                  style={{
                    height: 62,
                    borderBottom: '1px solid var(--ac-chat-border)',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '0 20px',
                    background: 'var(--ac-chat-card-solid)',
                    boxSizing: 'border-box',
                  }}
                >
                  {user && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '6px 14px 6px 10px',
                        borderRadius: 12,
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--ac-chat-border)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.20)',
                        flexShrink: 0,
                        maxWidth: 340,
                      }}
                      title={`Conectado como: ${user.nome} (${user.email || ''})`}
                    >
                      <UserAvatar
                        src={(user as any).fotoUrl}
                        name={user.nome}
                        role={user.cargo || user.perfil}
                        size="sm"
                        style={{ width: 34, height: 34, flexShrink: 0 }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <strong
                            style={{
                              fontSize: '0.80rem',
                              fontWeight: 750,
                              color: 'var(--ac-chat-text)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 150,
                            }}
                          >
                            {user.nome}
                          </strong>
                          <span
                            style={{
                              fontSize: '0.58rem',
                              fontWeight: 800,
                              padding: '1px 6px',
                              borderRadius: 4,
                              background: 'rgba(147, 51, 234, 0.25)',
                              color: '#c084fc',
                              border: '1px solid rgba(147, 51, 234, 0.45)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.02em',
                            }}
                          >
                            {user.perfil}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: '0.66rem',
                            color: 'var(--ac-chat-muted)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 220,
                          }}
                        >
                          {user.email || (user.cargo ? `Cargo: ${user.cargo}` : 'Online')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 32,
                    textAlign: 'center',
                    gap: 16,
                    color: 'var(--ac-chat-muted)',
                  }}
                >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '24px 24px 0 24px',
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.20), rgba(59, 130, 246, 0.35))',
                    border: '2px solid rgba(147, 51, 234, 0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(147, 51, 234, 0.30)',
                    overflow: 'hidden',
                    padding: 8,
                  }}
                >
                  <img
                    src="/icons/icon-192x192.png"
                    alt="Aluno Conectado"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '16px 16px 0 16px',
                    }}
                  />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--ac-chat-text)' }}>
                    Aluno Conectado Chat Interno
                  </h3>
                  <p style={{ margin: '6px 0 0', fontSize: '0.85rem', maxWidth: 380, lineHeight: 1.5 }}>
                    Selecione uma conversa ou contato ao lado para visualizar o histórico de mensagens e interagir em tempo real.
                  </p>
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 99,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--ac-chat-border)',
                    fontSize: '0.72rem',
                    color: 'var(--ac-chat-muted)',
                    marginTop: 10,
                  }}
                >
                  <Shield size={13} style={{ color: '#10b981' }} />
                  <span>Ambiente seguro, isolado e em conformidade com a LGPD</span>
                </div>
              </div>
            </>
          )}
          </main>
        </div>
      ) : (
        /* ═══ MODO NORMAL (WIDGET LATERAL / EMBUTIDO NO DASHBOARD) ═══ */
        <>
          {renderHeader()}

          {!isMinimized && (
            <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
              {renderSidebar()}

              <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {viewMode !== 'chat' && (
                  <ChatToolbar
                    searchText={
                      viewMode === 'anexos'
                        ? anexosSearch
                        : viewMode === 'historico'
                        ? historicoSearch
                        : searchText
                    }
                    onSearchChange={(val) => {
                      if (viewMode === 'anexos') setAnexosSearch(val);
                      else if (viewMode === 'historico') setHistoricoSearch(val);
                      else setSearchText(val);
                    }}
                    searchPlaceholder={
                      viewMode === 'contatos'
                        ? 'Buscar no catálogo de contatos...'
                        : viewMode === 'historico'
                        ? 'Pesquisar histórico por nome ou motivo...'
                        : viewMode === 'anexos'
                        ? 'Buscar fotos, vídeos, documentos...'
                        : 'Pesquisar ou começar uma nova conversa...'
                    }
                    showFilters={viewMode === 'conversas'}
                    activeGroup={activeGroup}
                    onGroupChange={(group) => setActiveGroup(group)}
                    activeSubFilter={activeSubFilter}
                    onSubFilterChange={(sub) => setActiveSubFilter(sub)}
                    onlyUnread={onlyUnread}
                    onOnlyUnreadChange={() => setOnlyUnread((prev) => !prev)}
                    onlyFavorites={onlyFavorites}
                    onOnlyFavoritesChange={() => setOnlyFavorites((prev) => !prev)}
                    onOpenContacts={() => setViewMode('contatos')}
                  />
                )}

                {renderStatusBar()}
                {renderFilterBar()}
                {renderTicketFlowBar()}
                {renderOpsPanel()}

                <section
                  className={viewMode === 'chat' ? 'ac-chat-panel-bg' : ''}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    background: viewMode === 'chat' ? undefined : 'var(--ac-chat-card-solid)',
                    position: 'relative',
                  }}
                >
                  {viewMode === 'chat' ? (
                    <>
                      {renderChatMessages()}
                      {renderComposer()}
                    </>
                  ) : viewMode === 'conversas' ? (
                    renderConversationList()
                  ) : viewMode === 'contatos' ? (
                    renderContactsList()
                  ) : viewMode === 'historico' ? (
                    renderHistoricoList()
                  ) : (
                    renderAnexosHub()
                  )}
                </section>

                {viewMode !== 'chat' && (
                  <div className="ac-chat-bottom-tabs">
                    <button
                      type="button"
                      onClick={() => setViewMode('conversas')}
                      className={`ac-chat-bottom-tab-btn ${viewMode === 'conversas' ? 'is-active' : ''}`}
                    >
                      <MessageSquare size={17} />
                      <span>Conversas</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('contatos')}
                      className={`ac-chat-bottom-tab-btn ${viewMode === 'contatos' ? 'is-active' : ''}`}
                    >
                      <Users size={17} />
                      <span>Contatos</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setViewMode('historico');
                        loadHistorico();
                      }}
                      className={`ac-chat-bottom-tab-btn ${viewMode === 'historico' ? 'is-active' : ''}`}
                    >
                      <Clock3 size={17} />
                      <span>Histórico</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setViewMode('anexos');
                        loadAnexos(anexosFilterTipo);
                      }}
                      className={`ac-chat-bottom-tab-btn ${viewMode === 'anexos' ? 'is-active' : ''}`}
                    >
                      <Paperclip size={17} />
                      <span>Anexos</span>
                    </button>
                  </div>
                )}
              </main>
            </div>
          )}
        </>
      )}

      {/* Modal Premium para Anexar Contato */}
      <Modal
        open={showAttachContactModal}
        onClose={() => setShowAttachContactModal(false)}
        title="Anexar Contato"
        size="default"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 320, maxHeight: '60vh' }}>
          <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Selecione um contato para anexá-lo à mensagem atual.
          </p>

          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Buscar contato..."
              value={attachContactSearch}
              onChange={(e) => setAttachContactSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 10px 9px 32px',
                borderRadius: 10,
                border: '1px solid var(--ac-chat-border)',
                background: 'var(--ac-chat-card-solid)',
                color: 'var(--ac-chat-text)',
                fontSize: '0.8rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div className="ac-chat-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1, paddingRight: 4 }}>
            {filteredContatosForAttach.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Nenhum contato encontrado.
              </div>
            ) : (
              filteredContatosForAttach.map((contato) => {
                const visual = getSegmentVisual(contato.perfil, contato.cargo);
                return (
                  <div
                    key={contato.id}
                    onClick={() => handleAttachContact(contato)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 12,
                      cursor: 'pointer',
                      border: '1px solid transparent',
                      background: 'rgba(255, 255, 255, 0.02)',
                      transition: 'background 150ms ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'; }}
                  >
                    <UserAvatar
                      src={contato.fotoUrl}
                      name={contato.nome}
                      role={contato.cargo || contato.perfil}
                      size={34}
                      style={{ flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                      <h4 style={{ margin: 0, fontSize: '0.78rem', fontWeight: 800, color: 'var(--ac-chat-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {contato.nome}
                      </h4>
                      <p style={{ margin: '2px 0 0', fontSize: '0.65rem', fontWeight: 650, color: visual.color }}>
                        {formatRole(contato.perfil, contato.cargo)}
                      </p>
                      {contato.escola?.nome && (
                        <p style={{ margin: '2px 0 0', fontSize: '0.62rem', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {contato.escola.nome}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--ac-chat-border)', paddingTop: 12 }}>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setShowAttachContactModal(false)}
              style={{
                padding: '8px 16px',
                borderRadius: 10,
                border: '1px solid var(--ac-chat-border)',
                background: 'transparent',
                color: 'var(--ac-chat-text)',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Premium para Transferir Atendimento */}
      <Modal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title="Transferir Atendimento"
        size="default"
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn-cancel" onClick={() => setShowTransferModal(false)}>
              Cancelar
            </button>
            <button
              className="btn-save"
              onClick={handleTransferSubmit}
              disabled={!selectedTransferId}
              style={{ opacity: !selectedTransferId ? 0.6 : 1 }}
            >
              Transferir
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
            Selecione o operador para quem deseja transferir este atendimento.
          </p>
          <div className="form-group">
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nome, perfil ou cargo..."
              value={transferSearch}
              onChange={(e) => setTransferSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          {loadingContatos ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <Loader2 className="ac-chat-spin" size={24} style={{ color: 'var(--ac-chat-primary)' }} />
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--ac-chat-border)', borderRadius: 6, padding: 6 }}>
              {filteredContatosForTransfer.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 12, fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                  Nenhum contato disponível para transferência.
                </div>
              ) : (
                filteredContatosForTransfer.map((c) => {
                  const isSelected = selectedTransferId === c.id;
                  return (
                    <div
                      key={c.id}
                      onClick={() => setSelectedTransferId(c.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: isSelected ? 'var(--ac-chat-primary)' : 'transparent',
                        color: isSelected ? '#fff' : 'inherit',
                        transition: 'background 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <UserAvatar
                          src={c.fotoUrl}
                          name={c.nome}
                          role={c.cargo || c.perfil}
                          size="sm"
                          style={{ flexShrink: 0 }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.nome}</span>
                          <span style={{ fontSize: '0.75rem', opacity: isSelected ? 0.8 : 0.6 }}>
                            {formatRole(c.perfil, c.cargo)} {c.escola?.nome ? `• ${c.escola.nome}` : ''}
                          </span>
                        </div>
                      </div>
                      {isSelected && <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Selecionado</span>}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Premium para Fechar Atendimento */}
      <Modal
        open={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        title="Fechar Atendimento"
        size="default"
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn-cancel" onClick={() => setShowCloseModal(false)}>
              Cancelar
            </button>
            <button className="btn-save" onClick={handleCloseSubmit}>
              Confirmar Fechamento
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
            Forneça o motivo do fechamento para fins de auditoria e relatórios de satisfação.
          </p>
          <div className="form-group">
            <label className="form-label">Motivo do Fechamento</label>
            <select
              className="form-select"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="Resolvido pelo atendimento">Resolvido pelo atendimento</option>
              <option value="Dúvida esclarecida">Dúvida esclarecida</option>
              <option value="Direcionado para a coordenação">Direcionado para a coordenação</option>
              <option value="Outro">Outro (especificar abaixo)</option>
            </select>
          </div>
          {closeReason === 'Outro' && (
            <div className="form-group">
              <label className="form-label">Especifique o Motivo</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Descreva detalhadamente o motivo..."
                value={closeCustomReason}
                onChange={(e) => setCloseCustomReason(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Premium para Escalar Atendimento */}
      <Modal
        open={showEscalateModal}
        onClose={() => setShowEscalateModal(false)}
        title="Escalar Atendimento"
        size="default"
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn-cancel" onClick={() => setShowEscalateModal(false)}>
              Cancelar
            </button>
            <button className="btn-save" onClick={handleEscalateSubmit}>
              Confirmar Escalação
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
            Esta ação marcará o atendimento com status URGENTE e notificará a equipe de supervisão.
          </p>
          <div className="form-group">
            <label className="form-label">Motivo da Escalação</label>
            <select
              className="form-select"
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="SLA/urgência operacional">SLA/urgência operacional</option>
              <option value="Suporte Técnico Nível 2">Suporte Técnico Nível 2</option>
              <option value="Necessidade de intervenção da diretoria">Necessidade de intervenção da diretoria</option>
              <option value="Outro">Outro (especificar abaixo)</option>
            </select>
          </div>
          {escalateReason === 'Outro' && (
            <div className="form-group">
              <label className="form-label">Especifique o Motivo</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Descreva detalhadamente a justificativa para escalação..."
                value={escalateCustomReason}
                onChange={(e) => setEscalateCustomReason(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Lightbox para Visualização Ampliada de Imagens */}
      {lightboxImage && (
        <Modal
          open={Boolean(lightboxImage)}
          onClose={() => setLightboxImage(null)}
          title={lightboxImage.title || 'Visualizar Imagem'}
          size="xl"
          footer={
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
              <a
                href={lightboxImage.src}
                download
                target="_blank"
                rel="noreferrer"
                className="btn-save"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Download size={14} /> Baixar Original
              </a>
              <button className="btn-cancel" onClick={() => setLightboxImage(null)}>
                Fechar
              </button>
            </div>
          }
        >
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: '75vh', overflow: 'hidden', padding: 8 }}>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.5)' }}
            />
          </div>
        </Modal>
      )}

      {/* Modal para Encaminhar Mensagens */}
      <Modal
        open={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        title="Encaminhar Mensagem(ns)"
        size="default"
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn-cancel" onClick={() => setShowForwardModal(false)}>
              Cancelar
            </button>
            <button
              className="btn-save"
              onClick={handleExecuteForward}
              disabled={forwarding || !forwardSelectedContacts.length}
            >
              {forwarding ? <Loader2 size={14} className="animate-spin" /> : `Encaminhar (${forwardSelectedContacts.length})`}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 320, maxHeight: '60vh' }}>
          <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Selecione os contatos para os quais deseja encaminhar {forwardMessageIds.length} mensagem(ns).
          </p>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Buscar destinatário..."
              value={forwardSearch}
              onChange={(e) => setForwardSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 10px 9px 32px',
                borderRadius: 10,
                border: '1px solid var(--ac-chat-border)',
                background: 'var(--ac-chat-card-solid)',
                color: 'var(--ac-chat-text)',
                fontSize: '0.78rem',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', maxHeight: 260, paddingRight: 4 }}>
            {contatos
              .filter((c) => !forwardSearch.trim() || c.nome.toLowerCase().includes(forwardSearch.toLowerCase()) || (c.cargo || '').toLowerCase().includes(forwardSearch.toLowerCase()))
              .map((contato) => {
                const isSelected = forwardSelectedContacts.includes(contato.id);
                return (
                  <div
                    key={contato.id}
                    onClick={() => {
                      setForwardSelectedContacts((prev) =>
                        isSelected ? prev.filter((id) => id !== contato.id) : [...prev, contato.id]
                      );
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      borderRadius: 10,
                      background: isSelected ? 'rgba(56, 189, 248, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                      border: isSelected ? '1px solid #38bdf8' : '1px solid var(--ac-chat-border)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ color: isSelected ? '#38bdf8' : 'var(--ac-chat-muted)' }}>
                      {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </div>
                    <UserAvatar src={contato.fotoUrl} name={contato.nome} role={contato.cargo || contato.perfil} size={30} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.80rem', fontWeight: 700, color: 'var(--ac-chat-text)' }}>{contato.nome}</div>
                      <div style={{ fontSize: '0.67rem', color: 'var(--ac-chat-muted)' }}>{formatRole(contato.perfil, contato.cargo)}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </Modal>

      {/* Modal para Confirmação de Exclusão Lógica de Mensagens */}
      <Modal
        open={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        title="Apagar Mensagem(ns)"
        size="default"
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn-cancel" onClick={() => setShowDeleteConfirmModal(false)}>
              Cancelar
            </button>
            <button
              className="btn-save"
              style={{ background: '#ef4444', borderColor: '#ef4444' }}
              onClick={handleExecuteDelete}
              disabled={deletingMessages}
            >
              {deletingMessages ? <Loader2 size={14} className="animate-spin" /> : 'Apagar para Todos'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--ac-chat-text)' }}>
            Deseja apagar {deleteTargetMessageIds.length} mensagem(ns) selecionada(s)?
          </p>
          <p style={{ margin: 0, fontSize: '0.74rem', color: 'var(--ac-chat-muted)', lineHeight: 1.4 }}>
            As mensagens serão removidas da visualização de todos os participantes e substituídas pelo aviso <em>"🚫 Esta mensagem foi apagada"</em>. Para fins de auditoria e conformidade, os registros permanecem com exclusão lógica segura.
          </p>
        </div>
      </Modal>

      {/* Modal para Silenciar Contato */}
      <Modal
        open={showMuteModal}
        onClose={() => setShowMuteModal(false)}
        title="Silenciar Notificações"
        size="default"
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn-cancel" onClick={() => setShowMuteModal(false)}>
              Fechar
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--ac-chat-muted)' }}>
            Selecione por quanto tempo deseja silenciar novas notificações deste contato:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              className="btn-cancel"
              style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--ac-chat-border)' }}
              onClick={() => muteTargetContactId && handleExecuteMute(muteTargetContactId, 8)}
            >
              <strong>Por 8 horas</strong>
            </button>
            <button
              type="button"
              className="btn-cancel"
              style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--ac-chat-border)' }}
              onClick={() => muteTargetContactId && handleExecuteMute(muteTargetContactId, 168)}
            >
              <strong>Por 1 semana</strong>
            </button>
            <button
              type="button"
              className="btn-cancel"
              style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--ac-chat-border)' }}
              onClick={() => muteTargetContactId && handleExecuteMute(muteTargetContactId, -1)}
            >
              <strong>Sempre (Até eu reativar)</strong>
            </button>
            {muteTargetContactId && mutedContactIds[muteTargetContactId] && (
              <button
                type="button"
                className="btn-save"
                style={{ textAlign: 'center', padding: '10px 14px', borderRadius: 10, marginTop: 6 }}
                onClick={() => handleExecuteMute(muteTargetContactId, 0)}
              >
                Reativar Notificações Agora
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
//#endregion AC_BLOCK global-chat-widget.global-chat-widget
